import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, CheckSquare, ArrowLeft, ArrowRight, Play, Square, Headphones, FileText, CheckCircle2, Bookmark, PenLine, AlertCircle, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { questionService, type Question } from "@/services/questionService";
import { JustifiedQuestionText } from "@/components/JustifiedQuestionText";
import { selectRandomItems } from "@/utils/testRandomizer";
import { historyService } from "@/services/historyService";
import { activityService } from "@/services/activityService";
import { testStateService } from "@/services/testStateService";
import { WRITING_STRUCTURE, getWritingConfigByKey, normalizeWritingSection, normalizeListeningSection } from "@/data/tefStructure";
import { mediaService } from "@/services/mediaService";
import { getCompetencyLabel, getTefScore, getCECRL, getNCLC } from "@/data/tefScoreScale";
import { useAuth } from "@/contexts/AuthContext";
import { extractQuestionFromBody } from "@/utils/questionValidation";
import { normalizeQuestionOptions, sortQuestionsByStableImportOrder, normalizeQuestionCompetency, getQuestionAudioUrl, getOptionImageUrl } from "@/utils/questionNormalizer";
import { getCleanSectionLabel, isWritingCompetency, isWritingSectionA, isWritingSectionB } from "@/utils/questionSectionUtils";
import { buildWritingTaskV2 } from "@/types/writingV2";
import { isBareMediaName } from "@/utils/mediaUrlResolver";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";
import { wordBankService } from "@/services/wordBankService";


const countWords = (text: string): number => {
  return text.trim().split(/\s+/).filter(Boolean).length;
};

function buildWritingV2TaskWithAnswer(task: any, answerText: string) {
  const userText = String(answerText || "").trim();
  const wordCount = countWords(userText);
  const minimumWordsRequired = task.section === "Section B" ? 200 : 80;

  return {
    ...task,
    userText,
    wordCount,
    minimumWordsRequired,
    meetsMinimumWordCount: wordCount >= minimumWordsRequired,
  };
}

const getDisplaySection = (q: any) => {
  if (!q) return "";
  if (q.competence?.toLowerCase() === "writing") {
    const key = q.sectionKey || normalizeWritingSection(q.section || "");
    return key === "section_a" ? "Section A" : "Section B";
  }
  return getCleanSectionLabel(q);
};

const getFallbackQuestionBySection = (q: any): string | null => {
  if (!q) return null;
  const section = q.section || "";
  const instruction = (q.instruction || "").toLowerCase();
  const bodyText = (q.text || q.body || q.content || q.context || "").trim();
  const title = (q.title || "").toLowerCase();

  // 1. Phrases/Textes Lacunaires
  if (section.includes("lacunaires") || section.includes("lacunaire") || title.includes("lacunaire")) {
    if (bodyText.includes("(___1)")) return "Quelle réponse complète correctement la lacune (___1) ?";
    if (bodyText.includes("(___2)")) return "Quelle réponse complète correctement la lacune (___2) ?";
    if (bodyText.includes("(___)")) {
      if (instruction.includes("phrase")) return "Quelle réponse complète correctement la phrase ?";
      return "Quelle réponse complète correctement la lacune ?";
    }
    // Fallback if gap found but not precisely matched
    if (bodyText.includes("___")) return "Quelle réponse complète correctement le texte ?";
  }

  // 2. Based on Instruction
  if (instruction.includes("choisissez le mot qui complète correctement la phrase")) {
    return "Quelle réponse complète correctement la phrase ?";
  }
  if (instruction.includes("indiquez le mot ou le groupe de mots manquant")) {
    if (bodyText.includes("___1") || bodyText.includes("(___1)")) return "Quelle réponse complète correctement la lacune (___1) ?";
    if (bodyText.includes("___2") || bodyText.includes("(___2)")) return "Quelle réponse complète corretamente la lacune (___2) ?";
    return "Quelle réponse complète corretamente la lacune?";
  }

  // 3. Lecture rapide
  if (section.includes("Lecture rapide") || section.includes("lecture rapide")) {
    // Check for Documents
    if (bodyText.includes("Document 1") && bodyText.includes("Document 2") && bodyText.includes("Document 3")) {
      return "Quel document correspond à l'information demandée ?";
    }
    // Check for Graphics
    if (q.hasImage || title.includes("graphique") || title.includes("analyse") || section.includes("graphiques")) {
      return "Observez le graphique et choisissez la bonne réponse.";
    }
  }

  return null;
};

const resolveDisplayedQuestion = (q: any) => {
  if (!q) return { question: null, body: "" };

  const bodyText = (q.text || q.body || q.content || q.context || "").trim();
  const extracted = extractQuestionFromBody(bodyText);

  const explicitQuestion =
    q.question?.trim() ||
    q.questionText?.trim() ||
    q.specificQuestion?.trim() ||
    q.prompt?.trim() ||
    q.statement?.trim() ||
    q.metadata?.question?.trim() ||
    q.metadata?.prompt?.trim();

  const fallbackQuestion = getFallbackQuestionBySection(q);

  const finalQuestion = explicitQuestion || extracted.extractedQuestion || fallbackQuestion || null;
  
  let finalBody = bodyText;
  if (!explicitQuestion && extracted.extractedQuestion) {
    finalBody = extracted.cleanBody;
  }

  return {
    question: finalQuestion,
    body: finalBody
  };
};

const getQuestionPrompt = (q: any) => {
  return resolveDisplayedQuestion(q).question || "";
};

const getVisibleContext = (q: any) => {
  if (!q) return "";
  return q.visibleContext || q.context || q.sourceText || q.metadata?.visibleContext || q.metadata?.context || "";
};

const isExplanationText = (text: string): boolean => {
  if (!text) return false;
  const lower = text.toLowerCase();
  const markers = [
    "la réponse correcte est",
    "resposta correta",
    "correta é",
    "explication",
    "justification",
    "analyse de l'expert",
    "l'analyse de l'expert",
    "d'après l'explication"
  ];
  return markers.some(m => lower.includes(m));
};

const cleanEEText = (text: string | undefined): string => {
  if (!text) return "";
  
  const lines = text.split("\n");
  const filteredLines = lines.map(line => {
    let trimmed = line.trim();
    const lower = trimmed.toLowerCase();
    
    // 1. If it's a standalone technical metadata line, filter it out completely
    if (
      lower.startsWith("section b type de document") || 
      lower.startsWith("section a type de document") ||
      lower.startsWith("type de document") ||
      lower.startsWith("consigne-type") ||
      lower.startsWith("objectif") ||
      lower === "consigne" ||
      lower === "consigne:" ||
      lower === "consigne :" ||
      lower === "consignes" ||
      lower === "consignes:" ||
      lower === "consignes :"
    ) {
      return "";
    }
    
    // 2. If it has a technical prefix with text following it, strip the prefix and keep the instruction content
    const prefixes = [
      /^(consigne-type\s*:\s*)/i,
      /^(consigne-type\s*)/i,
      /^(consignes\s*:\s*)/i,
      /^(consigne\s*:\s*)/i,
      /^(consignes\s*)/i,
      /^(consigne\s*)/i,
      /^(objectif\s*:\s*)/i,
      /^(objectif\s*)/i
    ];
    
    for (const prefixReg of prefixes) {
      if (prefixReg.test(trimmed)) {
        trimmed = trimmed.replace(prefixReg, "").trim();
        break;
      }
    }
    
    return trimmed;
  }).filter(line => line.length > 0);
  
  return filteredLines.join("\n").trim();
};

function normalizeAnswer(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toUpperCase();
}

function buildAnswerReview(currQuestions: any[], currAnswers: Record<string | number, string>) {
  return currQuestions.map((q, idx) => {
    const questionId = q.id;
    const userAnswer = currAnswers[questionId] ?? currAnswers[idx] ?? null;

    const correctAnswer =
      q.correctAnswer ??
      q.correct_answer ??
      q.answer ??
      q.gabarito ??
      q.correct ??
      null;

    const isAnswered = userAnswer !== null && String(userAnswer).trim() !== "";
    const isCorrect =
      isAnswered &&
      normalizeAnswer(userAnswer) === normalizeAnswer(correctAnswer);

    return {
      questionId,
      questionNumber: idx + 1,
      title: q.title ?? `Questão ${idx + 1}`,
      instruction: q.instruction ?? q.command ?? "",
      text: q.text ?? q.content ?? "",
      userAnswer: isAnswered ? userAnswer : null,
      correctAnswer,
      isAnswered,
      isCorrect,
      explanation: q.explanation ?? q.feedback ?? q.justification ?? "",
      options: normalizeQuestionOptions(q),
      competence: q.competence,
      section: q.section
    };
  });
}

function resolveAudioSrc(question: any, resolvedMediaUrls: Record<string, string>): string {
  if (!question) return "";
  const rawUrl = getQuestionAudioUrl(question);
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://") || rawUrl.startsWith("data:")) {
    return rawUrl;
  }
  return resolvedMediaUrls[rawUrl] || resolvedMediaUrls[question.audioMediaId] || "";
}

function resolveOptionImageSrc(question: any, opt: any, resolvedMediaUrls: Record<string, string>): string {
  if (!question) return "";
  const rawUrl = getOptionImageUrl(question, opt.label) || opt.image || opt.imageUrl || "";
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://") || rawUrl.startsWith("data:")) {
    return rawUrl;
  }
  return resolvedMediaUrls[rawUrl] || (opt.mediaId ? resolvedMediaUrls[opt.mediaId] : "");
}

function shouldRenderImageOptions(question: any): boolean {
  if (!question) return false;
  
  const normalize = (str: any) => 
    String(str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const competence = normalize(question.competence || question.competency);
  const section = normalize(question.section || question.sectionKey || question.audioType || question.subType || "");

  const isCO = competence.includes("comprehension orale") || competence === "co" || competence === "listening";
  const isImageSection = section.includes("dialogues associes a des images") || section === "section_a";

  if (isCO && isImageSection) return true;
  if (question.requiresOptionImages === true) return true;
  if (question.optionAImage || question.optionBImage || question.optionCImage || question.optionDImage) return true;
  
  if (Array.isArray(question.options) && question.options.some((o: any) => o.image || o.imageUrl || o.mediaId)) {
    return true;
  }

  return false;
}

function getQuestionTextAlternatives(question: any): Record<string, string> {
  if (!question) return {};

  const result: Record<string, string> = {};

  // 1. Try question.alternatives
  if (question.alternatives && typeof question.alternatives === "object" && !Array.isArray(question.alternatives)) {
    Object.entries(question.alternatives).forEach(([key, val]) => {
      if (val) result[key] = String(val);
    });
  }

  // 2. Try question.options if it is a plain object
  if (Object.keys(result).length === 0 && question.options && typeof question.options === "object" && !Array.isArray(question.options)) {
    Object.entries(question.options).forEach(([key, val]) => {
      if (val) result[key] = String(val);
    });
  }

  // 3. Try question.options if it is an array
  if (Object.keys(result).length === 0 && question.options && Array.isArray(question.options)) {
    question.options.forEach((opt: any) => {
      if (opt && typeof opt === "object") {
        const label = opt.label || opt.id || opt.key;
        const text = opt.text || opt.value || opt.content;
        if (label && text) {
          result[label] = String(text);
        }
      }
    });
  }

  // 4. Try separated fields: optionA, optionB, optionC, optionD
  if (Object.keys(result).length === 0) {
    const labels = ["A", "B", "C", "D"] as const;
    labels.forEach(l => {
      const fieldVal = question[`option${l}`] || question[`alternative${l}`];
      if (fieldVal) {
        result[l] = String(fieldVal);
      }
    });
  }

  // 5. Fallback: labels with empty texts
  if (Object.keys(result).length === 0) {
    const labels = ["A", "B", "C", "D"] as const;
    labels.forEach(l => {
      result[l] = "";
    });
  }

  return result;
}

function inferCompetencyFromQuestions(questions: any[]): "reading" | "listening" | "writing" {
  if (!Array.isArray(questions) || questions.length === 0) return "reading";
  if (questions.some(q => q.requiresAudio || q.audio || q.audioUrl || q.audioMediaId)) return "listening";
  if (questions.some(q => q.section?.includes("Expression") || q.minimumWordsRequired)) return "writing";
  return "reading";
}

function resolveTestCompetency({
  selectedCompetency,
  currentTest,
  questions,
}: any): "reading" | "listening" | "writing" {
  const values = [
    selectedCompetency,
    currentTest?.competency,
    currentTest?.testType,
    currentTest?.resultType,
    questions?.[0]?.competency,
  ].filter(Boolean).map(v => String(v).toLowerCase());

  if (values.some(v => v.includes("listening") || v.includes("orale") || v === "co")) {
    return "listening";
  }

  if (values.some(v => v.includes("compréhension écrite") || v.includes("reading") || v === "ce")) {
    return "reading";
  }

  if (values.some(v => v.includes("expression écrite") || v.includes("writing") || v === "ee")) {
    return "writing";
  }

  if (questions?.some((q: any) => q.requiresAudio || q.audio || q.audioUrl)) {
    return "listening";
  }

  if (questions?.some((q: any) => q.minimumWordsRequired || q.userText !== undefined)) {
    return "writing";
  }

  return "reading";
}

function normalizeQuestionMediaForRuntime(question: any) {
  if (!question) return question;

  const audioUrl = getQuestionAudioUrl(question);

  const optionAImage = getOptionImageUrl(question, "A");
  const optionBImage = getOptionImageUrl(question, "B");
  const optionCImage = getOptionImageUrl(question, "C");
  const optionDImage = getOptionImageUrl(question, "D");

  const normalizedOptions = normalizeQuestionOptions({
    ...question,
    optionAImage,
    optionBImage,
    optionCImage,
    optionDImage,
  }).map((opt: any) => {
    const label = String(opt.label || opt.id || opt.value || "").toUpperCase();
    return {
      ...opt,
      image: opt.image || getOptionImageUrl(question, label),
      imageUrl: opt.imageUrl || opt.image || getOptionImageUrl(question, label),
    };
  });

  return {
    ...question,
    audioUrl,
    audio: audioUrl,
    optionAImage,
    optionBImage,
    optionCImage,
    optionDImage,
    options: normalizedOptions,
    hasAudio: Boolean(audioUrl || question.audioMediaId),
    hasImage: Boolean(
      optionAImage ||
      optionBImage ||
      optionCImage ||
      optionDImage ||
      question.imageMediaId ||
      question.imageUrl ||
      normalizedOptions.some((o: any) => o.image || o.imageUrl)
    ),
  };
}

export default function TestRunner() {
  const { t } = useTranslation();
  const location = useLocation();

  console.log("[TEST_RUNNER_RENDER_SOURCE]", {
    source: "TestRunner.tsx",
    pathname: location.pathname,
    reason: location.pathname.includes("/test") ? "por rota" : "por estado global",
  });

  if (!location.pathname.includes("/test")) {
    console.warn("[TEST_RUNNER_WRONG_ROUTE_DETECTED]", {
      pathname: location.pathname,
    });
    return null;
  }

  console.log("[TEST_RUNNER_NORMAL_NAVIGATION_MODE]", {
    pathname: window.location.pathname,
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAdmin, profile, profileLoading } = useAuth();

  // Free Trial Limits Pre-flight Check on Mount / Hydration
  useEffect(() => {
    if (profileLoading) return;
    if (profile && profile.role !== "admin" && profile.plan !== "premium") {
      if (profile.trial_tests_used >= profile.trial_tests_limit) {
        alert(t("startTest.trialLimitReached", "Seu período gratuito chegou ao limite. Assine para continuar usando todos os recursos."));
        navigate("/upgrade");
      }
    }
  }, [profile, profileLoading, navigate, t]);

  const type = searchParams.get("type") || "reading";
  const mode = searchParams.get("mode") || "complete";
  const selectedSection = searchParams.get("section") || "";
  const practiceMode = searchParams.get("practiceMode") || "practice";
  const diagnosticMode = searchParams.get("diagnosticMode") === "true";

  // Telemetry check for first test started
  useEffect(() => {
    const checkFirstTest = async () => {
      try {
        const history = await historyService.getTestHistory();
        if (history.length === 0) {
          await activityService.trackUserActivity("first_test_started", "test", null, {
            type,
            mode,
            section: selectedSection,
            timestamp: new Date().toISOString()
          });
        }
      } catch (err) {
        console.warn("[TestRunner] Error checking for first test telemetry", err);
      }
    };
    checkFirstTest();
  }, []);

  // Full Exam States
  const [fullExamStage, setFullExamStage] = useState<"reading" | "listening" | "writing">("reading");
  const [fullExamStartedAt] = useState<number>(() => Date.now());
  const [readingResults, setReadingResults] = useState<any>(null);
  const [listeningResults, setListeningResults] = useState<any>(null);
  const [showTransition, setShowTransition] = useState<"to_listening" | "to_writing" | null>(null);

  const activeType = mode === "full_exam" ? fullExamStage : type;

  // Study Time Tracker hook integration
  const studyTrackerParams = (() => {
    if (activeType === "listening") {
      return { activityType: "listening_test", competency: "listening" };
    }
    if (activeType === "writing") {
      return { activityType: "writing_practice", competency: "writing" };
    }
    return { activityType: "reading_test", competency: "reading" };
  })();

  useStudyTimeTracker({
    activityType: studyTrackerParams.activityType,
    competency: studyTrackerParams.competency,
    section: selectedSection || null,
    enabled: !showTransition
  });


  // Normal States
  const [questions, setQuestions] = useState<any[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(3600); // 60 minutes
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [reviewMarked, setReviewMarked] = useState<Record<number, boolean>>({});

  // Audio preload states
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [preloadedAudios, setPreloadedAudios] = useState<Record<string, string>>({});
  
  // CO preloading states
  const [coPreloadStatus, setCoPreloadStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [coPreloadCurrent, setCoPreloadCurrent] = useState(0);
  const [coPreloadTotal, setCoPreloadTotal] = useState(0);
  const [coPreloadedUrls, setCoPreloadedUrls] = useState<Record<string, string>>({});
  const [coPreloadedMediaMap, setCoPreloadedMediaMap] = useState<Record<string, { audioReady: boolean; imagesReady: Record<string, boolean> }>>({});
  const [coPreloadErrorMessage, setCoPreloadErrorMessage] = useState<string | null>(null);

  const audioPreloadCache = useRef<Map<string, HTMLAudioElement>>(new Map());
  const audioLoadStartTimeRef = useRef<number>(0);
  const preloadedUrlsRef = useRef<Set<string>>(new Set());

  // Audio Preloading and Caching for Listening (CO) section
  useEffect(() => {
    if (activeType !== "listening" || questions.length === 0) return;
    let active = true;

    const resolveAudioUrlForPreload = async (q: any): Promise<{ key: string; url: string } | null> => {
      if (!q) return null;
      const rawUrl = getQuestionAudioUrl(q);
      if (!rawUrl) return null;
      if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://") || rawUrl.startsWith("data:")) {
        return { key: q.id || String(q.importOrder || q.metadata?.importOrder || q.temporaryImportId || q.title || q.text || ""), url: rawUrl };
      }
      const mediaId = q.audioMediaId || rawUrl;
      if (mediaId && !isBareMediaName(mediaId)) {
        const url = await mediaService.getMediaUrl(mediaId);
        if (url) {
          preloadedUrlsRef.current.add(url);
          return { key: q.id || String(q.importOrder || q.metadata?.importOrder || q.temporaryImportId || q.title || q.text || ""), url };
        }
      }
      return null;
    };

    const preloadAdjacentAudios = async () => {
      const targets = [
        questions[currentQuestionIdx],
        questions[currentQuestionIdx + 1],
        questions[currentQuestionIdx - 1],
      ].filter(Boolean).map(normalizeQuestionMediaForRuntime);

      console.log("[CO_AUDIO_PRELOAD_QUEUE]", targets.map(q => q.title || q.importOrder));
      const newUrls: Record<string, string> = { ...preloadedAudios };

      await Promise.all(targets.map(async (q) => {
        const qKey = q.id || String(q.importOrder || q.metadata?.importOrder || q.temporaryImportId || q.title || q.text || "");
        if (newUrls[qKey]) {
          const url = newUrls[qKey];
          if (!audioPreloadCache.current.has(url)) {
            try {
              const audioObj = new Audio();
              audioObj.src = url;
              audioObj.preload = "auto";
              audioObj.load();
              audioPreloadCache.current.set(url, audioObj);
            } catch (e) {
              console.warn("Failed to load audio obj in cache:", e);
            }
          }
          return;
        }

        try {
          const res = await resolveAudioUrlForPreload(q);
          if (res && active) {
            newUrls[res.key] = res.url;
            const audioObj = new Audio();
            audioObj.src = res.url;
            audioObj.preload = "auto";
            audioObj.load();
            audioPreloadCache.current.set(res.url, audioObj);
            console.log("[CO_AUDIO_PRELOADED_SUCCESS]", { title: q.title, url: res.url });
          }
        } catch (err) {
          console.error("Failed to preload audio:", q.title, err);
        }
      }));

      if (active) {
        setPreloadedAudios(prev => {
          const hasChanges = Object.keys(newUrls).some(k => prev[k] !== newUrls[k]) ||
                             Object.keys(prev).length !== Object.keys(newUrls).length;
          return hasChanges ? newUrls : prev;
        });
      }
    };

    preloadAdjacentAudios();
    return () => { active = false; };
  }, [questions, currentQuestionIdx, activeType]);

  // Clean up preloaded audio URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      audioPreloadCache.current.clear();
      preloadedUrlsRef.current.forEach(url => {
        if (url && !url.startsWith("http://") && !url.startsWith("https://") && !url.startsWith("data:")) {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            console.warn("Error revoking preloaded audio URL:", e);
          }
        }
      });
    };
  }, []);
  
  // Writing specific state
  const [writingText, setWritingText] = useState("");
  // Store writing text per task index so switching tasks preserves each task's text
  const [writingTexts, setWritingTexts] = useState<Record<number, string>>({});
  const [savedWords, setSavedWords] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Ref to bypass the accidental-exit blocker when finishing intentionally
  const isFinishingTestRef = useRef(false);

  // Modal B — unanswered questions confirmation
  const [showUnansweredModal, setShowUnansweredModal] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);

  // Modal C — minimum word count warning for writing tasks
  const [showWordWarningModal, setShowWordWarningModal] = useState(false);
  const [wordWarningData, setWordWarningData] = useState<{ current: number; minimum: number; onContinue: () => void } | null>(null);

  // Confirmation & AI Correction Modal states
  const [showConfirmFinishModal, setShowConfirmFinishModal] = useState(false);
  const [correctWithAI, setCorrectWithAI] = useState(true);
  const [isSavingAndCorrecting, setIsSavingAndCorrecting] = useState(false);
  const [aiErrorMessage, setAiErrorMessage] = useState<string | null>(null);

  const passedState = location.state as { testSessionId?: string, shuffleQuestions?: boolean } | null;
  const passedShuffleQuestions = passedState?.shuffleQuestions;
  const passedTestSessionId = passedState?.testSessionId;

  const [testAttemptId] = useState<string>(() => {
    const saved = testStateService.getPartialProgress() as any;
    if (saved && saved.testAttemptId) {
      return saved.testAttemptId;
    }
    if (passedTestSessionId) {
      sessionStorage.setItem("tef_current_test_attempt_id", passedTestSessionId);
      return passedTestSessionId;
    }
    const existing = sessionStorage.getItem("tef_current_test_attempt_id");
    if (existing) return existing;
    const newId = crypto.randomUUID();
    sessionStorage.setItem("tef_current_test_attempt_id", newId);
    return newId;
  });

  // Synchronize writing text for the current index
  const handleWritingTextChange = (val: string) => {
    setWritingText(val);
    setWritingTexts(prev => ({
      ...prev,
      [currentQuestionIdx]: val
    }));
  };

  useEffect(() => {
    const activeIsWriting = mode === "full_exam"
      ? fullExamStage === "writing"
      : type === "writing";
    if (activeIsWriting) {
      setWritingText(writingTexts[currentQuestionIdx] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIdx, mode, fullExamStage, type]);

  // Reset audio states on question change
  useEffect(() => {
    setAudioLoading(false);
    setAudioReady(false);
    setAudioError(null);
  }, [currentQuestionIdx]);

  // Timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatSpentTimeShort = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Guards to prevent re-entrant loops
  const hasRestoredSavedProgress = useRef(false);
  const isTransitioningStage = useRef(false);

  // ── Effect 1: Run ONCE on mount to restore any saved progress ──────────────
  // This is the ONLY place we read from sessionStorage to hydrate fullExamStage.
  useEffect(() => {
    // Reset any stale finishing flag from a previous session
    testStateService.setIsFinishingTest(false);

    const state = location.state as any;
    if (state?.forceNewSession) {
      testStateService.clearPartialProgress();
    } else {
      const saved = testStateService.getPartialProgress();
      if (
        saved &&
        saved.type === type &&
        saved.mode === mode &&
        saved.selectedSection === selectedSection
      ) {
        hasRestoredSavedProgress.current = true;
        // Restore stage first (synchronously mutates ref before state triggers Effect 2)
        if (saved.fullExamStage) {
          isTransitioningStage.current = true; // tell Effect 2 this is a restore, not a user advance
          setFullExamStage(saved.fullExamStage);
        }
        setCurrentQuestionIdx(saved.currentQuestionIdx);
        setSelectedAnswers(saved.selectedAnswers);
        setReviewMarked(saved.reviewMarked);
        setWritingText(saved.writingText);
        setTimeLeft(saved.timeLeft);
        if (saved.readingResults) setReadingResults(saved.readingResults);
        if (saved.listeningResults) setListeningResults(saved.listeningResults);
      }
    }
    testStateService.setTestInProgress(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← intentionally empty: runs once on mount only

  // saved.fullExamStage (that only happens in Effect 1 above).
  useEffect(() => {
    const loadStageQuestions = async () => {
      setQuestionsLoading(true);
      const activeCompetence = mode === "full_exam"
        ? (fullExamStage === "reading" ? "Reading" : (fullExamStage === "listening" ? "Listening" : "Writing"))
        : (type.toLowerCase() === "reading" ? "Reading" : (type.toLowerCase() === "listening" ? "Listening" : "Writing"));

      console.log("[QuestionService] loading questions for test", {
        competence: activeCompetence,
        section: selectedSection || "Geral"
      });

      try {
        const all = await questionService.getQuestions();
        let filtered: any[] = [];

        const randomSeed = `${testAttemptId}-${Date.now()}-${Math.random()}`;

        if (diagnosticMode) {
          filtered = questionService.generateDiagnosticTest(all, activeCompetence, randomSeed);
        } else if (mode === "section") {
          if (activeCompetence === "Reading") {
            filtered = questionService.generateReadingTest(all, selectedSection, practiceMode, randomSeed);
          } else if (activeCompetence === "Writing") {
            const pool = all.filter(q => {
              if (q.competence !== activeCompetence) return false;
              const normalized = questionService.getNormalizedSection(q);
              return normalized.toLowerCase() === selectedSection.toLowerCase();
            });
            filtered = selectRandomItems(pool, 1);
            console.log("[EE_V2_BUILD_SECTION]", {
              section: selectedSection,
              poolSize: pool.length,
              selectedCount: filtered.length,
            });
          } else {
            filtered = all.filter(q => {
              if (q.competence !== activeCompetence) return false;
              const normalized = questionService.getNormalizedSection(q);
              return normalized.toLowerCase() === selectedSection.toLowerCase();
            });
          }
        } else if (activeCompetence === "Reading") {
          filtered = questionService.generateReadingTest(all, "all", practiceMode, randomSeed);
        } else if (activeCompetence === "Listening" && mode !== "section") {
          filtered = questionService.generateListeningTest(all, randomSeed);
        } else if (activeCompetence === "Writing" && mode !== "section") {
          filtered = questionService.generateWritingTest(all, randomSeed);
        } else {
          filtered = all.filter(q => {
            if (q.competence !== activeCompetence) return false;
            return true;
          });
          
          console.log("[START_TEST_SELECTED_FILTERS]", {
            selectedCompetency: activeCompetence,
            normalizedCompetency: normalizeQuestionCompetency(activeCompetence),
            selectedSection: selectedSection,
            normalizedSelectedSection: activeCompetence === "Listening" ? normalizeListeningSection(selectedSection || "") : selectedSection,
            mode,
            amount: searchParams.get("limit"),
          });

          console.log("[START_TEST_QUESTIONS_FOUND]", filtered.map((q: any) => ({
            id: q.id,
            title: q.title,
            competency: q.competency || q.competence,
            normalizedCompetency: normalizeQuestionCompetency(q.competency || q.competence),
            section: q.section,
            normalizedSection: activeCompetence === "Listening" ? normalizeListeningSection(q.section) : q.section,
            hasAudio: Boolean(getQuestionAudioUrl(q)),
            requiresAudio: q.requiresAudio,
            requiresOptionImages: q.requiresOptionImages,
          })));

          filtered.forEach((q: any) => {
            if (q.id?.startsWith("fb_") || q.id?.startsWith("mock_") || q.title?.toLowerCase().includes("generic") || q.title?.toLowerCase().includes("mock")) {
              console.warn("[START_TEST_MOCK_QUESTION_DETECTED]", q);
            }
          });
        }

        if (filtered.length === 0) {
          console.warn("[TestRunner] No questions found for current criteria", { activeCompetence, selectedSection });
        }

        const limitParam = searchParams.get("limit");
        
        // Se estiver em exam mode (simulado/diagnóstico) a aleatorização já aconteceu dentro dos generators em questionService.ts.
        // O shuffle manual aqui se aplica mais ao Treino Livre, onde as perguntas vêm como lista simples.
        // IMPORTANTE: Para Expression Écrite (EE), NUNCA re-embaralhar o array final, pois a Section A DEVE vir antes da Section B.
        if (practiceMode !== "exam" && !diagnosticMode && !isWritingCompetency(activeCompetence)) {
          let shuffleQuestions = true;
          if (passedShuffleQuestions !== undefined) {
             shuffleQuestions = passedShuffleQuestions;
          } else {
             const configStr = localStorage.getItem("tef_trainer_settings");
             const config = configStr ? JSON.parse(configStr) : {};
             shuffleQuestions = config.shuffle !== false;
          }

          let count: number | null = null;
          if (limitParam && limitParam !== "all" && activeCompetence !== "Writing") {
            const parsed = parseInt(limitParam, 10);
            if (!isNaN(parsed) && parsed > 0) {
              count = parsed;
            }
          }
          
          filtered = selectRandomItems(filtered, count);
        }

        filtered = sortQuestionsByStableImportOrder(filtered);

        console.log("[CO_TEST_RUNNER_LOADED_ORDER]", filtered.map((q, index) => ({
          arrayIndex: index,
          id: q.id,
          importOrder: q.importOrder || q.metadata?.importOrder,
          questionNumber: q.questionNumber || q.metadata?.questionNumber,
          title: q.title,
          audio: q.audio,
          audioUrl: q.audioUrl,
          competency: q.competency || q.metadata?.competency || q.competence,
        })));

        setQuestions(filtered);

        // [TEST_RUNNER_CO_MEDIA_DEBUG]
        try {
          const listeningTestQuestions = filtered.filter((q: any) => {
            const comp = String(q.competency || q.competence || "").toLowerCase();
            return comp === "listening" || comp === "co" || comp.includes("orale") || comp.includes("listening");
          });
          console.log("[TEST_RUNNER_CO_MEDIA_DEBUG] Active CO test questions:", listeningTestQuestions.map((q: any) => ({
            id: q.id,
            title: q.title,
            section: q.section,
            resolvedAudio: getQuestionAudioUrl(q),
            resolvedOptions: q.options?.map((opt: any) => ({
              label: opt.label,
              image: getOptionImageUrl(q, opt.label)
            }))
          })));
        } catch (errDebug) {
          console.error("[TEST_RUNNER_CO_MEDIA_DEBUG] Error logging active CO questions:", errDebug);
        }

        console.log("[TestBuilder] MODE:", mode);
        console.log("[TestBuilder] TEST TYPE:", type);
        console.log("[TestBuilder] COMPETENCY:", activeCompetence);
        console.log("[TestBuilder] SECTION:", selectedSection || "all");
        console.log("[TestBuilder] POOL SIZE:", all.length);
        console.log("[TestBuilder] SELECTED COUNT:", filtered.length);
        console.log("[TestBuilder] SELECTED IDS:", filtered.map(q => q.id));
        console.log("[TestBuilder] SELECTED TITLES:", filtered.map(q => q.title));

        if (activeCompetence === "Writing" && filtered.length > 1) {
          console.log("[EE_V2_COMPLETE_RUNNER_STATE]", {
            currentQuestionIdx,
            totalQuestions: filtered.length,
            sections: filtered.map(q => q.section),
          });
        }

        // When the stage advances (user clicked "Continue to Listening/Writing"),
        // reset per-stage UI state. But do NOT reset if this is the initial restore.
        if (isTransitioningStage.current) {
          setCurrentQuestionIdx(0);
          setSelectedAnswers({});
          setReviewMarked({});
          setWritingText("");
          setWritingTexts({});
          setTimeLeft(3600);
          isTransitioningStage.current = false;
        }
      } catch (err) {
        console.error("[QuestionService] failed to load questions", err);
      } finally {
        setQuestionsLoading(false);
      }
    };

    loadStageQuestions();
  }, [type, mode, selectedSection, fullExamStage, testAttemptId]);

  const runCoPreloading = async (loadedQuestions: any[]) => {
    if (activeType !== "listening" || loadedQuestions.length === 0) {
      setCoPreloadStatus("idle");
      return;
    }

    setCoPreloadStatus("loading");
    setCoPreloadCurrent(0);
    setCoPreloadTotal(loadedQuestions.length);
    setCoPreloadErrorMessage(null);

    const mediaMap: Record<string, { audioReady: boolean; imagesReady: Record<string, boolean> }> = {};
    const urlsMap: Record<string, string> = {};
    const errors: string[] = [];        // Critical errors: missing/null URLs — block test start
    const warnings: string[] = [];      // Soft errors: preload cache failures — warn but allow test

    const preloadImage = (url: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => reject(new Error(`Image load timeout: ${url}`)), 5000);
        img.onload = () => { clearTimeout(timeout); resolve(); };
        img.onerror = () => { clearTimeout(timeout); reject(new Error(`Failed to load image: ${url}`)); };
        img.src = url;
      });
    };

    const preloadAudio = (url: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.preload = "auto";
        const timeout = setTimeout(() => {
          // Resolve anyway after 8s — audio URL is valid, just slow to buffer
          audio.oncanplaythrough = null;
          audio.onerror = null;
          resolve();
        }, 8000);
        audio.oncanplaythrough = () => { clearTimeout(timeout); resolve(); };
        audio.onerror = () => { clearTimeout(timeout); reject(new Error(`Failed to load audio: ${url}`)); };
        audio.src = url;
        audio.load();
      });
    };

    const resolveMediaUrl = async (keyOrUrl: string): Promise<string | null> => {
      if (!keyOrUrl) return null;
      if (keyOrUrl.startsWith("http://") || keyOrUrl.startsWith("https://") || keyOrUrl.startsWith("data:")) {
        return keyOrUrl;
      }
      try {
        const url = await mediaService.getMediaUrl(keyOrUrl);
        if (url && !url.startsWith("http://") && !url.startsWith("https://") && !url.startsWith("data:")) {
          preloadedUrlsRef.current.add(url);
        }
        return url || null;
      } catch (err) {
        console.error("Error resolving media URL:", keyOrUrl, err);
        return null;
      }
    };

    for (let i = 0; i < loadedQuestions.length; i++) {
      const q = loadedQuestions[i];
      setCoPreloadCurrent(i + 1);

      const qId = q.id || String(q.importOrder || i);
      mediaMap[qId] = {
        audioReady: false,
        imagesReady: {}
      };

      // 1. Resolve Audio URL
      const rawAudio = getQuestionAudioUrl(q) || q.audio || q.media?.audioUrl || q.metadata?.audioUrl || q.audioMediaId;
      let audioUrl: string | null = null;
      if (rawAudio) {
        audioUrl = await resolveMediaUrl(rawAudio);
        if (audioUrl) {
          urlsMap[rawAudio] = audioUrl;
          if (q.audioMediaId) {
            urlsMap[q.audioMediaId] = audioUrl;
          }
        }
      }

      // Check if audio is missing (critical — can't play at all)
      if (!audioUrl) {
        errors.push(`Questão ${i + 1} (${q.title || qId}): Áudio ausente ou inválido.`);
      }

      // 2. Resolve Option Images
      const imagePromises: { label: string; url: string; promise: Promise<void> }[] = [];
      const opts = normalizeQuestionOptions(q);
      const requiresImages = shouldRenderImageOptions(q);

      for (const opt of opts) {
        const label = String(opt.label || opt.id || "").toUpperCase();
        const rawImg = getOptionImageUrl(q, label) || opt.image || opt.imageUrl || (opt.mediaId ? opt.mediaId : "");
        let imgUrl: string | null = null;
        if (rawImg) {
          imgUrl = await resolveMediaUrl(rawImg);
          if (imgUrl) {
            urlsMap[rawImg] = imgUrl;
            if (opt.mediaId) {
              urlsMap[opt.mediaId] = imgUrl;
            }
          }
        }

        if (requiresImages && !imgUrl) {
          errors.push(`Questão ${i + 1} (${q.title || qId}): Imagem da alternativa ${label} ausente.`);
        }

        if (imgUrl) {
          imagePromises.push({
            label,
            url: imgUrl,
            promise: preloadImage(imgUrl)
          });
        }
      }

      // 3. Perform preloading
      const audioPromise = audioUrl ? preloadAudio(audioUrl) : Promise.resolve();
      
      const results = await Promise.allSettled([
        audioPromise,
        ...imagePromises.map(ip => ip.promise)
      ]);

      const audioResult = results[0];
      const imagesResults = results.slice(1);

      if (audioResult.status === "fulfilled" && audioUrl) {
        mediaMap[qId].audioReady = true;
      } else if (audioUrl) {
        // Soft warning: URL was resolved but browser couldn't cache it. Audio will still load on playback.
        warnings.push(`Questão ${i + 1} (${q.title || qId}): Pré-carregamento do áudio incompleto (carregará ao reproduzir).`);
        console.warn("[CO_PRELOAD_AUDIO_CACHE_MISS]", { id: qId, title: q.title, url: audioUrl });
      }

      imagePromises.forEach((ip, idx) => {
        const res = imagesResults[idx];
        if (res.status === "fulfilled") {
          mediaMap[qId].imagesReady[ip.label] = true;
        } else {
          // Soft warning: image URL resolved but browser couldn't cache it.
          warnings.push(`Questão ${i + 1} (${q.title || qId}): Pré-carregamento de imagem ${ip.label} incompleto.`);
          console.warn("[CO_PRELOAD_IMAGE_CACHE_MISS]", { id: qId, label: ip.label, url: ip.url });
        }
      });
    }

    if (errors.length > 0) {
      // Critical errors: some media URLs couldn't be resolved — block test start
      const allMessages = [...errors, ...(warnings.length > 0 ? [`\n⚠️ Avisos adicionais: ${warnings.length}`] : [])].join("\n");
      setCoPreloadErrorMessage(allMessages);
      setCoPreloadStatus("error");
      console.error("[CO_PRELOAD_FAILED_CRITICAL]", errors);
    } else {
      // No critical errors — proceed even if some caching failed
      setCoPreloadedMediaMap(mediaMap);
      setCoPreloadedUrls(urlsMap);
      setCoPreloadStatus("ready");
      if (warnings.length > 0) {
        console.warn("[CO_PRELOAD_READY_WITH_WARNINGS]", warnings);
      } else {
        console.log("[CO_PRELOAD_SUCCESS] Preloaded all media successfully", { mediaMap, urlsMap });
      }
    }
  };

  useEffect(() => {
    if (activeType === "listening" && questions.length > 0) {
      runCoPreloading(questions);
    } else {
      setCoPreloadStatus("idle");
    }
  }, [questions, activeType]);

  // Save progress dynamically as user works
  useEffect(() => {
    if (questions.length > 0 && testStateService.isTestInProgress) {
      testStateService.savePartialProgress({
        type,
        mode,
        selectedSection,
        fullExamStage,
        timeLeft,
        currentQuestionIdx,
        selectedAnswers,
        reviewMarked,
        writingText,
        fullExamStartedAt,
        readingResults,
        listeningResults,
        testAttemptId
      } as any);
    }
  }, [
    questions,
    type,
    mode,
    selectedSection,
    fullExamStage,
    timeLeft,
    currentQuestionIdx,
    selectedAnswers,
    reviewMarked,
    writingText,
    fullExamStartedAt,
    readingResults,
    listeningResults
  ]);

  const runtimeQuestions = questions.map(normalizeQuestionMediaForRuntime);
  const currentQuestion = runtimeQuestions[currentQuestionIdx];
  const progressPercent = runtimeQuestions.length > 0 ? ((currentQuestionIdx + 1) / runtimeQuestions.length) * 100 : 0;

  // Debug active question fields
  useEffect(() => {
    if (currentQuestion) {
      console.log("CURRENT QUESTION DEBUG", currentQuestion);
      const extracted = extractQuestionFromBody(currentQuestion.text || currentQuestion.body || "");
      console.log("[TestRunner] Detailed field check:", {
        id: currentQuestion.id,
        title: currentQuestion.title,
        question: currentQuestion.question,
        questionText: currentQuestion.questionText,
        specificQuestion: currentQuestion.specificQuestion,
        prompt: currentQuestion.prompt,
        statement: currentQuestion.statement,
        body: currentQuestion.body,
        text: currentQuestion.text,
        metadata: currentQuestion.metadata,
        fallbackExtracted: extracted.extractedQuestion
      });
    }
  }, [currentQuestion]);

  // RAW questions log for CO debug
  useEffect(() => {
    if (questions.length > 0) {
      const isCO = questions.some(q => {
        const comp = String(q.competency || q.competence || "").toLowerCase();
        return comp === "listening" || comp === "co" || comp.includes("orale") || comp.includes("listening");
      });
      if (isCO) {
        console.log("[CO_TEST_MEDIA_RAW_QUESTIONS]", questions.map((q, index) => ({
          index,
          id: q.id,
          title: q.title,
          section: q.section,
          competency: q.competency,

          audio: q.audio,
          audioUrl: q.audioUrl,
          audio_url: q.audio_url,

          optionAImage: q.optionAImage,
          optionBImage: q.optionBImage,
          optionCImage: q.optionCImage,
          optionDImage: q.optionDImage,

          option_a_image: q.option_a_image,
          option_b_image: q.option_b_image,
          option_c_image: q.option_c_image,
          option_d_image: q.option_d_image,

          media: q.media,
          metadata: q.metadata,
          options: q.options,
          content: q.content,
        })));
      }
    }
  }, [questions]);

  // Runtime questions media resolved log for CO debug in development
  useEffect(() => {
    if (import.meta.env.DEV && runtimeQuestions.length > 0) {
      const isCO = runtimeQuestions.some(q => {
        const comp = String(q.competency || q.competence || "").toLowerCase();
        return comp === "listening" || comp === "co" || comp.includes("orale") || comp.includes("listening");
      });
      if (isCO) {
        console.log("[CO_RUNTIME_MEDIA_RESOLVED]", runtimeQuestions.map(q => ({
          id: q.id,
          title: q.title,
          audioUrl: q.audioUrl,
          images: {
            A: q.optionAImage,
            B: q.optionBImage,
            C: q.optionCImage,
            D: q.optionDImage,
          },
          options: q.options?.map((o: any) => ({
            label: o.label,
            image: o.image,
            imageUrl: o.imageUrl,
          })),
        })));
      }
    }
  }, [runtimeQuestions]);

  // Resolved media URLs for current question in the TestRunner
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState<Record<string, string>>({});

  // Resolve media for current question in the TestRunner
  useEffect(() => {
    let active = true;
    const urlsToRevoke: string[] = [];

    const resolveMedia = async () => {
      if (!currentQuestion) {
        setResolvedMediaUrls({});
        return;
      }

      const resolved: Record<string, string> = {};

      // 1. Resolve imageMediaId
      if (currentQuestion.imageMediaId) {
        const url = await mediaService.getMediaUrl(currentQuestion.imageMediaId);
        if (url && active) {
          resolved[currentQuestion.imageMediaId] = url;
          urlsToRevoke.push(url);
        }
      }

      // 2. Resolve audioMediaId
      if (currentQuestion.audioMediaId) {
        const url = await mediaService.getMediaUrl(currentQuestion.audioMediaId);
        if (url && active) {
          resolved[currentQuestion.audioMediaId] = url;
          urlsToRevoke.push(url);
        }
      }

      // 3. Resolve options mediaIds
      if (currentQuestion.options) {
        for (const opt of currentQuestion.options) {
          if (opt.mediaId) {
            const url = await mediaService.getMediaUrl(opt.mediaId);
            if (url && active) {
              resolved[opt.mediaId] = url;
              urlsToRevoke.push(url);
            }
          }
        }
      }

      if (active) {
        setResolvedMediaUrls(resolved);
      }
    };

    resolveMedia();

    return () => {
      active = false;
      urlsToRevoke.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn("Error revoking object URL:", e);
        }
      });
    };
  }, [currentQuestion]);

  const handleNext = () => {
    if (currentQuestionIdx < questions.length - 1) {
      setCurrentQuestionIdx(currentQuestionIdx + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx(currentQuestionIdx - 1);
    }
  };

  const hasEE = (mode === "full_exam" && fullExamStage === "writing") || type === "writing";

  const executeFinishTest = async (useAI: boolean, forceLocalFallbackOnError = false) => {
    if (isSavingAndCorrecting) {
      console.warn("[TEST_FINISH_CLICK_BLOCKED] already saving/finishing");
      return;
    }

    const typeLower = (type || "").toLowerCase();
    const activeCompetence = mode === "full_exam"
      ? (fullExamStage === "reading" ? "Reading" : (fullExamStage === "listening" ? "Listening" : "Writing"))
      : (typeLower === "reading" || typeLower.includes("ce") || typeLower.includes("compréhension écrite") || typeLower.includes("comprehension ecrite") ? "Reading" : 
         (typeLower === "listening" || typeLower.includes("co") || typeLower.includes("compréhension orale") || typeLower.includes("comprehension orale") ? "Listening" : "Writing"));

    let finalCompetency = resolveTestCompetency({
      selectedCompetency: type,
      currentTest: { competency: activeCompetence, testType: mode, resultType: type },
      questions
    });
    
    // Normalize to standard english enum to prevent URL mapping bugs in French/Portuguese
    const finalCompLower = (finalCompetency || "").toLowerCase();
    if (finalCompLower.includes("ce") || finalCompLower.includes("reading") || finalCompLower.includes("compréhension écrite") || finalCompLower.includes("comprehension ecrite")) {
      finalCompetency = "reading";
    } else if (finalCompLower.includes("co") || finalCompLower.includes("listening") || finalCompLower.includes("compréhension orale") || finalCompLower.includes("comprehension orale")) {
      finalCompetency = "listening";
    } else {
      finalCompetency = "writing";
    }

    console.log("[TestRunner] finishing test", {
      competence: activeCompetence,
      section: selectedSection || "Geral",
      userEmail: user?.email,
      isAdmin: !!isAdmin
    });

    console.log("[FinishTest] authenticated user", {
      hasUser: Boolean(user),
      userId: user?.id
    });

    console.log("[FinishTest] start", {
      testMode: mode,
      competence: type,
      hasQuestions: Boolean(questions?.length),
      answersCount: Object.keys(selectedAnswers || {}).length
    });

    setIsSavingAndCorrecting(true);
    setAiErrorMessage(null);

    try {
      // 1. Combine writing texts (safe fallback for EE or full_exam EE stage)
      let combinedText = "";
      let totalWords = 0;
      questions.forEach((q, idx) => {
        const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
        const wordsCount = qText.trim().split(/\s+/).filter(Boolean).length;
        totalWords += wordsCount;
        
        if (qText.trim()) {
          const displaySec = getDisplaySection(q);
          combinedText += `=== ${displaySec} ===\n\n${qText.trim()}\n\n`;
        }
      });
      
      if (!combinedText.trim()) {
        combinedText = writingText;
        totalWords = writingText.trim().split(/\s+/).filter(Boolean).length;
      } else {
        combinedText = combinedText.trim();
      }

      const finishedAt = new Date().toISOString();
      const timeSpentSecs = mode === "full_exam" 
        ? Math.round((Date.now() - fullExamStartedAt) / 1000) 
        : (3600 - (timeLeft || 3600));

      const safeAttemptId = testAttemptId || crypto.randomUUID();

      // 2. Build test result object based on competence and mode
      const activeCompetence = mode === "full_exam"
        ? (fullExamStage === "reading" ? "Reading" : (fullExamStage === "listening" ? "Listening" : "Writing"))
        : (type.toLowerCase() === "reading" ? "Reading" : (type.toLowerCase() === "listening" ? "Listening" : "Writing"));

      let testContext = null;

      if (mode === "full_exam") {
        testContext = {
          competence: "Simulado Completo",
          testType: "complete",
          durationSeconds: timeSpentSecs,
          attemptKey: safeAttemptId,
          answers: {
            ce: readingResults?.answers || {},
            co: listeningResults?.answers || {},
            ee: {
              sectionA: questions[0] ? {
                questionId: questions[0].id,
                title: questions[0].title || "",
                instruction: questions[0].instruction || "",
                answer: writingTexts[0] || "",
                wordCount: (writingTexts[0] || "").trim().split(/\s+/).filter(Boolean).length
              } : null,
              sectionB: questions[1] ? {
                questionId: questions[1].id,
                title: questions[1].title || "",
                instruction: questions[1].instruction || "",
                answer: writingTexts[1] || "",
                wordCount: (writingTexts[1] || "").trim().split(/\s+/).filter(Boolean).length
              } : null
            }
          },
          result: {
            ce: readingResults,
            co: listeningResults,
            ee: {
              status: "correction_pending",
              score: null,
              maxScore: 450,
              nclcLevel: null,
              cefrLevel: null,
              wordCount: totalWords,
              correction: null
            },
            total: {
              score: null,
              maxScore: null,
              percentage: null
            }
          }
        };
      } else if (activeCompetence === "Writing") {
        // Find if we have both sections (A + B)
        const hasA = questions.some(q => isWritingSectionA(q));
        const hasB = questions.some(q => isWritingSectionB(q));
        const isCompleteEE = hasA && hasB;

        const normSecName = isCompleteEE ? "Teste completo EE" : 
                           (currentQuestion ? questionService.getNormalizedSection(currentQuestion) : (selectedSection || "Tâche 1"));
        
        // Build map of answers by section
        const eeAnswers: any = {
          sectionA: null,
          sectionB: null
        };

        questions.forEach((q, idx) => {
          const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
          const qWords = qText.trim() === "" ? 0 : qText.trim().split(/\s+/).length;
          const isA = isWritingSectionA(q);
          const isB = isWritingSectionB(q);
          
          const answerData = {
            questionId: q.id,
            title: q.title || "",
            instruction: q.instruction || q.command || "",
            answer: qText,
            wordCount: qWords,
            section: isA ? "Section A" : (isB ? "Section B" : "Section A")
          };

          if (isA) eeAnswers.sectionA = answerData;
          else if (isB) eeAnswers.sectionB = answerData;
          else if (!eeAnswers.sectionA) eeAnswers.sectionA = answerData; // Fallback
        });

        testContext = {
          competence: "Writing",
          testType: isCompleteEE ? "writing_v2_complete" : "competence",
          sectionName: normSecName,
          durationSeconds: timeSpentSecs,
          attemptKey: safeAttemptId,
          words: totalWords,
          text: combinedText,
          answers: eeAnswers,
          result: {
            ee: {
              status: "correction_pending",
              score: null,
              maxScore: 450, // Standard TEF EE max score is 450 for the entire competency (Task A + B)
              nclcLevel: null,
              cefrLevel: null,
              wordCount: totalWords,
              correction: null
            }
          }
        };

        if (isCompleteEE) {
          const writingTasksArray = [];
          if (eeAnswers.sectionA) writingTasksArray.push(eeAnswers.sectionA);
          if (eeAnswers.sectionB) writingTasksArray.push(eeAnswers.sectionB);

          console.log("[EE_V2_COMPLETE_FINISH_PAYLOAD]", {
            taskCount: writingTasksArray.length,
            sections: writingTasksArray.map(t => t.section),
            wordCounts: writingTasksArray.map(t => t.wordCount),
          });
        }
      } else {
        // CE or CO MCQ flow
        let score = 0;
        const sectionResults = {};
        
        questions.forEach((q, idx) => {
          const normSec = questionService.getNormalizedSection(q);
          if (!sectionResults[normSec]) {
            sectionResults[normSec] = { correct: 0, total: 0, percent: 0 };
          }
          sectionResults[normSec].total++;
          
          const ans = selectedAnswers[idx];
          const correct = q.correctAnswer ?? q.correct ?? q.correct_answer ?? q.answer ?? q.gabarito ?? null;
          if (ans !== undefined && normalizeAnswer(ans) === normalizeAnswer(correct)) {
            score++;
            sectionResults[normSec].correct++;
          }
        });

        Object.keys(sectionResults).forEach(sec => {
          const res = sectionResults[sec];
          res.percent = res.total > 0 ? Math.round((res.correct / res.total) * 100) : 0;
        });

        const percentScore = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
        const properComp = activeCompetence === "Reading" ? "Reading" : "Listening";
        const compKeyForScore = activeCompetence === "Reading" ? "reading" : "listening";

        const nclcEstimate = getNCLC(compKeyForScore, score, questions.length);
        const cecrlEstimate = getCECRL(compKeyForScore, score, questions.length);
        const normSecName = selectedSection || (questions[0] ? questionService.getNormalizedSection(questions[0]) : "Geral");

        const answerReview = buildAnswerReview(runtimeQuestions, selectedAnswers);
        console.log("[FinishTest] answerReview", answerReview);

        testContext = {
          competence: properComp,
          testType: mode === "section" ? "section" : "competence",
          sectionName: normSecName,
          score: score,
          maxScore: questions.length,
          percentage: percentScore,
          nclcLevel: nclcEstimate,
          cefrLevel: cecrlEstimate,
          totalQuestions: questions.length,
          correctAnswers: score,
          durationSeconds: timeSpentSecs,
          answers: {
            questions: questions.map(q => ({
              id: q.id,
              title: q.title || "",
              section: q.section || "",
              sectionKey: q.sectionKey || ""
            })),
            userAnswers: selectedAnswers,
            sectionResults: sectionResults,
            answerReview: answerReview
          },
          result: {
            score: score,
            maxScore: questions.length,
            percentage: percentScore,
            nclcLevel: nclcEstimate,
            cefrLevel: cecrlEstimate,
            correctAnswers: score,
            totalQuestions: questions.length,
            sectionResults: sectionResults,
            answerReview: answerReview
          }
        };
      }

      console.log("[FinishTest] clicked", { testMode: mode, competence: activeCompetence, section: selectedSection });

      const finalResult = historyService.buildTestHistoryPayload(testContext);

      console.log("[FinishTest] history payload", {
        testType: finalResult.testType,
        competence: finalResult.competence,
        section: finalResult.section,
        attemptKey: finalResult.attemptKey,
        hasAnswers: Boolean(finalResult.answers),
        hasResult: Boolean(finalResult.result)
      });

      // 3. Save via history service IMMEDIATELY (creates or updates preliminary entry)
      let savedRecord: any = null;
      let historySaveError = false;

      console.log("[TEST_FINISH_SAVE_ATTEMPT]", finalResult);

      try {
        savedRecord = await historyService.createOrUpdateTestHistory(finalResult);
        console.log("[TEST_FINISH_SAVE_RESULT]", { error: null, data: savedRecord });
        if (savedRecord && savedRecord.id) {
          sessionStorage.setItem("currentWritingHistoryId", savedRecord.id);
          localStorage.setItem("currentWritingHistoryId", savedRecord.id);
          console.log("[EE_CURRENT_HISTORY_ID_SET]", savedRecord.id);
        }
      } catch (dbErr: any) {
        historySaveError = true;
        console.error("[FinishTest] failed with full error:", dbErr);
        console.log("[TEST_FINISH_SAVE_RESULT]", { error: dbErr, data: null });
        if (dbErr?.message && dbErr.message.toLowerCase().includes("row-level security")) {
          console.error("[HistoryService] RLS policy error on test_history", dbErr);
        } else {
          console.error("[FinishTest] Supabase error details:", {
            message: dbErr?.message,
            code: dbErr?.code,
            details: dbErr?.details,
            hint: dbErr?.hint
          });
        }
        // Safe fallback record so UI never crashes on undefined .id
        savedRecord = { id: safeAttemptId, attempt_key: safeAttemptId };
      }
      
      // 4. Proceed with AI correction if requested (Writing tasks only)
      let writingCorrection: any = null;
      let totalScore = 0;
      let totalMaxScore = 0;
      let nclcLevel = "N/A";
      let cefrLevel = "N/A";
      let criteria: any[] = [];
      let generalFeedback = "";
      let sourceUsed = "local";

      if (hasEE && useAI && !forceLocalFallbackOnError && type !== "writing") {
        try {
          const { requestWritingCorrection } = await import("@/services/writingCorrectionService");
          const { getOrCreateClientId } = await import("@/utils/clientId");
          
          const corrections: Record<string, any> = {};
          let lastPersistedRecord: any = null;

          // Loop over the questions to correct them
          for (let idx = 0; idx < questions.length; idx++) {
            const q = questions[idx];
            const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
            if (qText.trim().length > 0) {
              const isSecB = q.sectionKey === "section_b" || String(q.section || "").toLowerCase().includes("b");
              const label = isSecB ? "Section B" : "Section A";
              
              const payload = {
                section: label,
                instruction: q.instruction || q.title || "",
                userText: qText,
                clientId: getOrCreateClientId(),
                historyId: savedRecord?.id || safeAttemptId,
                writingTasks: questions.map(q => ({ id: q.id, title: q.title || "", instruction: q.instruction || q.command || "" })),
                existingMetadata: finalResult.metadata
              };
              
              const res = await requestWritingCorrection(payload);
              console.log("[EE_CORRECTION_READY_TO_SAVE]", JSON.stringify(res?.correction, null, 2));
              
              if (res && res.persistedHistoryRecord) {
                lastPersistedRecord = res.persistedHistoryRecord;
              }

              if (res.success && res.correction) {
                corrections[isSecB ? "sectionB" : "sectionA"] = {
                  ...res.correction,
                  originalText: qText,
                  instruction: q.instruction || q.title || ""
                };
                totalScore += res.correction.estimatedScore || 0;
                totalMaxScore += res.correction.maxScore || 450;
                nclcLevel = res.correction.nclcEstimate || nclcLevel;
                cefrLevel = res.correction.cecrlEstimate || cefrLevel;
                const criterionArray = res.correction.criteria 
                  ? Object.values(res.correction.criteria) 
                  : [];
                criteria = [...criteria, ...criterionArray];
                generalFeedback += `${label}:\n${res.correction.overallComment || ""}\n\n`;
                sourceUsed = res.source || "gemini";
              } else {
                throw new Error(res.message || `Erro ao corrigir a ${label}`);
              }
            }
          }
          
          writingCorrection = {
            score: totalScore,
            maxScore: totalMaxScore,
            nclcLevel,
            cefrLevel,
            correction: corrections,
            criteria,
            generalFeedback: generalFeedback.trim(),
            source: sourceUsed
          };

          // Build updated payload including correction details
          let updatedResult: any = null;
          if (mode === "full_exam") {
            updatedResult = {
              ...finalResult,
              id: savedRecord?.id || safeAttemptId,
              score: writingCorrection.score, // Numeric score (e.g. 315)
              maxScore: writingCorrection.maxScore, // 450
              nclcLevel: writingCorrection.nclcLevel,
              cefrLevel: writingCorrection.cefrLevel,
              status: "concluido",
              metadata: {
                ...(finalResult.metadata || {}),
                writingCorrection: writingCorrection,
                nclc: writingCorrection.nclcLevel,
                cefr: writingCorrection.cefrLevel,
                totalScore: writingCorrection.score,
                maxScore: writingCorrection.maxScore
              },
              sections: {
                ...finalResult.sections,
                writing: {
                  text: combinedText,
                  words: totalWords,
                  correction: writingCorrection
                }
              }
            };
          } else {
            updatedResult = {
              ...finalResult,
              id: savedRecord?.id || safeAttemptId,
              score: writingCorrection.score,
              maxScore: writingCorrection.maxScore,
              percentage: Math.round((writingCorrection.score / writingCorrection.maxScore) * 100),
              status: "concluido",
              metadata: {
                ...(finalResult.metadata || {}),
                writingCorrection: writingCorrection,
                nclc: writingCorrection.nclcLevel,
                cefr: writingCorrection.cefrLevel,
                totalScore: writingCorrection.score,
                maxScore: writingCorrection.maxScore
              },
              result: {
                score: writingCorrection.score,
                maxScore: writingCorrection.maxScore,
                nclcLevel: writingCorrection.nclcLevel,
                cefrLevel: writingCorrection.cefrLevel,
                correction: writingCorrection.correction,
                criteria: writingCorrection.criteria,
                generalFeedback: writingCorrection.generalFeedback,
                source: writingCorrection.source,
                words: totalWords,
                text: combinedText,
                timeUsed: timeSpentSecs
              }
            };
          }
          try {
            savedRecord = await historyService.createOrUpdateTestHistory(updatedResult);
            console.log("[TestRunner] Saved final result with score:", updatedResult?.score);
          } catch (dbErr: any) {
            historySaveError = true;
            console.error("[FinishTest] failed with full error:", dbErr);
          }
        } catch (err: any) {
          console.error("AI correction failed, but preliminary progress is already saved:", err);
          setAiErrorMessage(err.message || "Não foi possível se conectar ao servidor de IA.");
          setShowConfirmFinishModal(false);

          // Cleanup before navigation
          sessionStorage.removeItem("tef_current_test_attempt_id");
          isFinishingTestRef.current = true;
          testStateService.setIsFinishingTest(true);
          testStateService.setTestInProgress(false);
          testStateService.clearPartialProgress();

          // Navigate safely so student doesn't lose progress
          const catchResType = finalCompetency === "writing" ? "writing_v2" : finalCompetency;
          const catchTType = mode === "full_exam" ? "complete" : (finalCompetency === "writing" ? "writing_v2_single" : finalCompetency);
          console.log("[CO_TEST_FINISH_STATE]", {
            finalCompetency,
            resultType: catchResType,
            testType: catchTType,
            questions: questions.map((q, index) => ({
              arrayIndex: index,
              id: q.id,
              importOrder: q.importOrder || q.metadata?.importOrder || (index + 1),
              title: q.title,
              hasAudio: Boolean(q.audio || q.audioUrl || q.media?.audio),
              competency: q.competency || q.metadata?.competency || q.competence,
            })),
          });

          if (mode === "full_exam") {
            navigate(`/test/result?mode=full_exam&id=${savedRecord?.id || ""}${historySaveError ? "&dbError=true" : ""}`);
          } else if (finalCompetency === "writing") {
            navigate(`/test/result?type=writing&words=${totalWords}&time=${timeSpentSecs}&text=${encodeURIComponent(combinedText)}&id=${savedRecord?.id || ""}&aiError=true${historySaveError ? "&dbError=true" : ""}`);
          } else {
            navigate(`/test/result?type=${finalCompetency}&id=${savedRecord?.id || ""}${historySaveError ? "&dbError=true" : ""}`);
          }
          return;
        }
      } else if (hasEE && (useAI || forceLocalFallbackOnError) && type !== "writing") {
        // Local simulated fallback
        const mockCorr: Record<string, any> = {};
        questions.forEach((q, idx) => {
          const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
          const isSecB = q.sectionKey === "section_b" || String(q.section || "").toLowerCase().includes("b");
          const label = isSecB ? "Section B" : "Section A";
          mockCorr[isSecB ? "sectionB" : "sectionA"] = {
            score: 225,
            nclcLevel: "NCLC 5",
            cefrLevel: "B1",
            originalText: qText,
            instruction: q.instruction || q.title || "",
            criteria: [
              { name: "Adequação", score: 3, maxScore: 5, comments: "Bom cumprimento da tarefa." }
            ],
            generalFeedback: "Correção simulada localmente (servidor offline)."
          };
          totalScore += 225;
          totalMaxScore += 450;
        });

        writingCorrection = {
          score: totalScore,
          maxScore: totalMaxScore,
          nclcLevel: "NCLC 5",
          cefrLevel: "B1",
          correction: mockCorr,
          criteria: [],
          generalFeedback: "Correção simulada localmente (servidor offline).",
          source: forceLocalFallbackOnError ? "mock_fallback" : "local"
        };

        let updatedResult: any = null;
        if (mode === "full_exam") {
          updatedResult = {
            ...finalResult,
            id: savedRecord?.id || safeAttemptId,
            score: `CE: ${readingResults?.percent || 0}% | CO: ${listeningResults?.percent || 0}% | EE: ${writingCorrection.nclcLevel}`,
            sections: {
              ...finalResult.sections,
              writing: {
                text: combinedText,
                words: totalWords,
                correction: writingCorrection
              }
            }
          };
        } else {
          updatedResult = {
            ...finalResult,
            id: savedRecord?.id || safeAttemptId,
            score: `${writingCorrection.score}/${writingCorrection.maxScore}`,
            percentage: Math.round((writingCorrection.score / writingCorrection.maxScore) * 100),
            status: "Corrigido com IA",
            result: {
              score: writingCorrection.score,
              maxScore: writingCorrection.maxScore,
              nclcLevel: writingCorrection.nclcLevel,
              cefrLevel: writingCorrection.cefrLevel,
              correction: writingCorrection.correction,
              criteria: writingCorrection.criteria,
              generalFeedback: writingCorrection.generalFeedback,
              source: writingCorrection.source,
              words: totalWords,
              text: combinedText,
              timeUsed: timeSpentSecs
            }
          };
        }
        try {
          savedRecord = await historyService.createOrUpdateTestHistory(updatedResult);
        } catch (dbErr: any) {
          historySaveError = true;
          console.error("[FinishTest] failed with full error (fallback mock):", dbErr);
        }
      }

      // Diagnostic computed results logging
      console.log("[FinishTest] computed reading", readingResults);
      console.log("[FinishTest] computed listening", listeningResults);
      console.log("[FinishTest] computed writing", writingCorrection);

      // 5. Cleanup and Navigate
      sessionStorage.removeItem("tef_current_test_attempt_id");
      isFinishingTestRef.current = true;
      testStateService.setIsFinishingTest(true);
      testStateService.setTestInProgress(false);
      testStateService.clearPartialProgress();

      const writingTasks = questions.map((q: any, idx: number) => {
        const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
        const section = q.section || q.subsection || q.title || (idx === 0 ? "Section A" : "Section B");
        const validSection = section.includes("B") ? "Section B" : "Section A";
        
        const baseTask = buildWritingTaskV2({
          id: q.id || q.questionId,
          section: validSection as any,
          title: q.title || q.promptTitle || "",
          prompt: q.prompt || q.instruction || q.question || q.text || "",
          userText: qText,
        });

        const taskWithAnswer = buildWritingV2TaskWithAnswer(baseTask, qText);
        taskWithAnswer.historyRecordId = savedRecord?.id || safeAttemptId;
        taskWithAnswer.attemptKey = safeAttemptId;
        return taskWithAnswer;
      });

      console.log("[EE_V2_NORMALIZED_WRITING_TASKS]", writingTasks.map(t => ({
        id: t.id,
        section: t.section,
        userTextLength: t.userText?.length || 0,
        wordCount: t.wordCount,
      })));

      const hasWritingText = writingTasks.some(task =>
        String(task.userText || "").trim().length > 0
      );

      console.log("[FINISH_EE_HAS_WRITING_TEXT]", hasWritingText);

      setShowConfirmFinishModal(false);

      const hasA = questions.some(q => isWritingSectionA(q));
      const hasB = questions.some(q => isWritingSectionB(q));
      const isCompleteEE = hasA && hasB;
      const resType = finalCompetency === "writing" ? "writing_v2" : finalCompetency;
      const tType = mode === "full_exam" ? "complete" : (finalCompetency === "writing" ? (isCompleteEE ? "writing_v2_complete" : "writing_v2_single") : finalCompetency);

      console.log("[TEST_FINISH_COMPETENCY_RESOLVED]", {
        selectedCompetency: type || null,
        currentTestCompetency: activeCompetence || null,
        inferredCompetency: inferCompetencyFromQuestions(questions),
        finalCompetency: finalCompetency,
        resultType: resType,
        testType: tType,
        questionSummary: questions.map(q => ({
          id: q.id,
          section: q.section,
          competency: q.competency || q.competence || null,
          requiresAudio: q.requiresAudio,
          hasAudio: Boolean(q.audio || q.audioUrl || q.audioMediaId),
          minimumWordsRequired: q.minimumWordsRequired,
        })),
      });

      console.log("[CO_TEST_FINISH_STATE]", {
        finalCompetency,
        resultType: resType,
        testType: tType,
        questions: questions.map((q, index) => ({
          arrayIndex: index,
          id: q.id,
          importOrder: q.importOrder || q.metadata?.importOrder || (index + 1),
          title: q.title,
          hasAudio: Boolean(q.audio || q.audioUrl || q.media?.audio),
          competency: q.competency || q.metadata?.competency || q.competence,
        })),
      });

      if (mode === "full_exam") {
        setTimeout(() => {
          try {
            const url = `/test/result?mode=full_exam&id=${savedRecord?.id || ""}${historySaveError ? "&dbError=true" : ""}`;
            try {
              sessionStorage.setItem("tef_test_result_fallback_state", JSON.stringify({
                resultType: "complete",
                testType: "complete",
                competency: "complete",
                result: finalResult,
                readingResults,
                listeningResults,
                writingCorrection,
                historyRecord: savedRecord
              }));
            } catch(e) {
              console.error("Failed to save fallback state", e);
            }
            console.log("[TestRunner] Forcing navigation to:", url);
            const a = document.createElement("a");
            a.href = url;
            document.body.appendChild(a);
            a.click();
          } catch(err: any) {
            alert("Erro crítico ao tentar redirecionar: " + (err?.message || "Desconhecido"));
            window.location.href = "/dashboard";
          }
        }, 100);
      } else if (finalCompetency === "writing") {
        const stateToSend = { 
          resultType: "writing_v2",
          competency: "writing",
          writingVersion: 2,
          testType: isCompleteEE ? "writing_v2_complete" : "writing_v2_single",
          section: isCompleteEE ? "Teste completo EE" : (questions[0]?.section || "Section A"),
          writingTasks,
          isOfficialEvaluation: false,
          createdAt: new Date().toISOString(),
          historyId: savedRecord?.id || safeAttemptId,
          historyRecord: savedRecord
        };

        console.log("[EE_V2_FINISH_NAVIGATION_PAYLOAD]", {
          testMode: mode,
          competency: type,
          section: isCompleteEE ? "Teste completo EE" : (questions[0]?.section || "Section A"),
          selectedQuestions: questions,
          writingAnswersByTaskId: writingTexts,
          normalizedWritingTasks: writingTasks,
          stateToSend,
        });

        setTimeout(() => {
          try {
            const url = `/test/result?type=writing&id=${savedRecord?.id || ""}${historySaveError ? "&dbError=true" : ""}`;
            try {
              sessionStorage.setItem("tef_test_result_fallback_state", JSON.stringify(stateToSend));
            } catch(e) {
              console.error("Failed to save fallback state", e);
            }
            console.log("[TestRunner] Forcing navigation to:", url);
            const a = document.createElement("a");
            a.href = url;
            document.body.appendChild(a);
            a.click();
          } catch(err: any) {
            alert("Erro crítico ao tentar redirecionar: " + (err?.message || "Desconhecido"));
            window.location.href = "/dashboard";
          }
        }, 100);
      } else {
        const mcqScore = finalResult.correctAnswers || 0;
        const answerReviewToPass = finalResult?.result?.answerReview || testContext?.result?.answerReview || [];
        setTimeout(() => {
          try {
            const url = `/test/result?type=${finalCompetency}&score=${mcqScore}&total=${questions.length}&id=${savedRecord?.id || ""}${historySaveError ? "&dbError=true" : ""}`;
            try {
              sessionStorage.setItem("tef_test_result_fallback_state", JSON.stringify({
                resultType: finalCompetency,
                testType: finalCompetency,
                competency: finalCompetency,
                result: finalResult,
                answerReview: answerReviewToPass,
                questions: questions,
                answers: selectedAnswers,
                competence: finalCompetency,
                section: selectedSection,
                historyRecord: savedRecord
              }));
            } catch(e) {
              console.error("Failed to save fallback state", e);
            }
            console.log("[TestRunner] Forcing navigation to:", url);
            const a = document.createElement("a");
            a.href = url;
            document.body.appendChild(a);
            a.click();
          } catch(err: any) {
            alert("Erro crítico ao tentar redirecionar: " + (err?.message || "Desconhecido"));
            window.location.href = "/dashboard";
          }
        }, 100);
      }
    } catch (error: any) {
      console.error("[TestRunner] finish failed", error);
      console.error("[TestRunner] Supabase details", {
        message: error?.message || error?.toString(),
        code: error?.code,
        details: error?.details,
        hint: error?.hint
      });
      alert(t("testRunner.finishTestError", "⚠️ Não foi possível finalizar o teste. Veja o console para detalhes técnicos."));
    } finally {
      setIsSavingAndCorrecting(false);
    }
  };

  const handleFinish = () => {
    // Bypass the accidental-exit blocker — this is an intentional finish
    isFinishingTestRef.current = true;
    testStateService.setIsFinishingTest(true);

    if (mode === "full_exam") {
      if (fullExamStage === "reading") {
        let score = 0;
        const sectionResults: Record<string, { correct: number; total: number; percent: number }> = {};
        
        questions.forEach((q, idx) => {
          const normSec = questionService.getNormalizedSection(q);
          if (!sectionResults[normSec]) {
            sectionResults[normSec] = { correct: 0, total: 0, percent: 0 };
          }
          sectionResults[normSec].total++;
          
          const ans = selectedAnswers[idx];
          const correct = q.correctAnswer || q.correct;
          if (ans === correct) {
            score++;
            sectionResults[normSec].correct++;
          }
        });
        
        // Calculate percentages
        Object.keys(sectionResults).forEach(sec => {
          const res = sectionResults[sec];
          res.percent = res.total > 0 ? Math.round((res.correct / res.total) * 100) : 0;
        });

        const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
        const nclcLevel = getNCLC("reading", score, questions.length);
        const cefrLevel = getCECRL("reading", score, questions.length);

        const answerReview = buildAnswerReview(runtimeQuestions, selectedAnswers);

        setReadingResults({
          total: questions.length,
          correct: score,
          percent: pct,
          percentage: pct,
          sectionResults,
          answers: selectedAnswers,
          nclcLevel,
          cefrLevel,
          correctAnswers: score,
          totalQuestions: questions.length,
          answerReview
        });
        setShowTransition("to_listening");
      } else if (fullExamStage === "listening") {
        let score = 0;
        const sectionResults: Record<string, { correct: number; total: number; percent: number }> = {};
        
        questions.forEach((q, idx) => {
          const normSec = questionService.getNormalizedSection(q);
          if (!sectionResults[normSec]) {
            sectionResults[normSec] = { correct: 0, total: 0, percent: 0 };
          }
          sectionResults[normSec].total++;
          
          const ans = selectedAnswers[idx];
          const correct = q.correctAnswer || q.correct;
          if (ans === correct) {
            score++;
            sectionResults[normSec].correct++;
          }
        });
        
        // Calculate percentages
        Object.keys(sectionResults).forEach(sec => {
          const res = sectionResults[sec];
          res.percent = res.total > 0 ? Math.round((res.correct / res.total) * 100) : 0;
        });

        const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
        const nclcLevel = getNCLC("listening", score, questions.length);
        const cefrLevel = getCECRL("listening", score, questions.length);

        const answerReview = buildAnswerReview(runtimeQuestions, selectedAnswers);

        setListeningResults({
          total: questions.length,
          correct: score,
          percent: pct,
          percentage: pct,
          sectionResults,
          answers: selectedAnswers,
          nclcLevel,
          cefrLevel,
          correctAnswers: score,
          totalQuestions: questions.length,
          answerReview
        });
        setShowTransition("to_writing");
      } else {
        // EE step of full exam completed
        setShowConfirmFinishModal(true);
      }
    } else {
      // Single competency finish flow
      if (type === "writing") {
        setShowConfirmFinishModal(true);
      } else {
        // MCQ directly completes
        executeFinishTest(false);
      }
    }
  };

  const handleSelectAnswer = (letter: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIdx]: letter
    });
  };

  const handleToggleReview = () => {
    setReviewMarked({
      ...reviewMarked,
      [currentQuestionIdx]: !reviewMarked[currentQuestionIdx]
    });
  };

  /** Returns the number of unanswered questions for the current test stage (0 = all done). */
  const getUnansweredCount = (): number => {
    const activeIsWriting = mode === "full_exam"
      ? fullExamStage === "writing"
      : type === "writing";
    if (activeIsWriting) {
      // Writing: any non-empty text counts as answered
      return writingText.trim().length === 0 ? 1 : 0;
    }
    return questions.filter((_, idx) => selectedAnswers[idx] === undefined).length;
  };

  /**
   * Entry-point for the Finish button.
   * 1. If writing and below minimum words → Modal C (word warning).
   * 2. Else if unanswered questions → Modal B (unanswered).
   * 3. Otherwise → finish directly.
   */
  const handleFinishButtonClick = () => {
    console.log("[TEST_FINISH_CLICK]", {
      testType: mode,
      competency: type,
      currentQuestionIndex: currentQuestionIdx,
      totalQuestions: questions.length,
      answersCount: Object.keys(selectedAnswers || {}).length,
      isFinishing: isSavingAndCorrecting
    });

    if (isSavingAndCorrecting) {
      return;
    }

    const activeIsWriting = mode === "full_exam"
      ? fullExamStage === "writing"
      : type === "writing";

    if (activeIsWriting) {
      const validationErrors: string[] = [];
      questions.forEach((q, idx) => {
        const qText = writingTexts[idx] || (idx === currentQuestionIdx ? writingText : "");
        const wordCount = qText.trim() === "" ? 0 : qText.trim().split(/\s+/).filter(Boolean).length;
        const isA = isWritingSectionA(q) || (
          !isWritingSectionB(q) &&
          String(q.section || "").toLowerCase().includes("section a")
        );
        const minWords = isA ? 80 : 200;
        const sectionName = isA ? "Section A" : "Section B";
        
        if (!qText.trim()) {
          validationErrors.push(`A ${sectionName} não foi preenchida.`);
        } else if (wordCount < minWords) {
          validationErrors.push(`A ${sectionName} possui apenas ${wordCount} palavras (mínimo de ${minWords}).`);
        }
      });

      if (validationErrors.length > 0) {
        alert("⚠️ Validação da Redação:\n\n" + validationErrors.join("\n") + "\n\nPor favor, complete a redação seguindo as regras antes de finalizar.");
        return;
      }
    }

    const missing = getUnansweredCount();
    if (missing > 0) {
      setUnansweredCount(missing);
      setShowUnansweredModal(true);
      return;
    }
    handleFinish();
  };


  // Word extraction function for Saving Word/Phrase selected in Writing
  const handleSaveSelectedText = async () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end).trim();

    if (selectedText) {
      try {
        setSavedWords((prev) => [...prev, selectedText]);
        
        await wordBankService.createWord({
          term: selectedText,
          meaning: "Salvo durante simulação",
          context: writingText,
          category: "Indefinida",
          tags: ["runner-saved"]
        });
        
        alert(t("testRunner.wordSavedSuccess", "Termo \"{{word}}\" salvo no Banco de Palavras!", { word: selectedText }));
      } catch (err: any) {
        console.error("[TestRunner] Error saving word to word bank", err);
        if (err.message && err.message.includes("já está cadastrada")) {
          alert("Esta palavra/frase já está cadastrada no Banco de Palavras.");
        } else {
          alert("Erro ao salvar a palavra/frase no Banco de Palavras. Por favor, tente novamente.");
        }
      }
    } else {
      alert(t("testRunner.selectWordAlert", "Selecione uma palavra ou frase no texto para salvar."));
    }
  };

  const handleContinueToListening = () => {
    // Signal Effect 2 to reset per-stage state (questionIdx, answers, timer)
    isTransitioningStage.current = true;
    setShowTransition(null);
    setFullExamStage("listening");
  };

  const handleContinueToWriting = () => {
    isTransitioningStage.current = true;
    setShowTransition(null);
    setFullExamStage("writing");
  };

  // Transition screen overlays
  if (showTransition === "to_listening") {
    return (
      <div className="max-w-md mx-auto py-12 animate-in fade-in duration-500">
        <Card className="border-none shadow-xl bg-white p-6 rounded-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mx-auto text-3xl">
            <CheckCircle2 className="w-8 h-8 text-purple-600" />
          </div>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">Compréhension Écrite (CE) Concluído</CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              Você finalizou a primeira etapa do Simulado Completo TEF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-semibold text-gray-700">Próxima etapa: Compréhension Orale (CO).</p>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleContinueToListening} 
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl py-6 font-bold shadow-md"
            >
              Continuar para Compréhension Orale (CO)
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (showTransition === "to_writing") {
    return (
      <div className="max-w-md mx-auto py-12 animate-in fade-in duration-500">
        <Card className="border-none shadow-xl bg-white p-6 rounded-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto text-3xl">
            <CheckCircle2 className="w-8 h-8 text-blue-600" />
          </div>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">Compréhension Orale (CO) Concluído</CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              Você finalizou a segunda etapa do Simulado Completo TEF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-semibold text-gray-700">Próxima etapa: Expression Écrite (EE).</p>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleContinueToWriting} 
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl py-6 font-bold shadow-md"
            >
              Continuar para Expression Écrite (EE)
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (questionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-48 space-y-4">
        <div className="w-12 h-12 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
        <p className="text-sm font-medium text-gray-500 animate-pulse">Carregando questões do estágio de teste...</p>
      </div>
    );
  }

  if (activeType === "listening" && coPreloadStatus === "loading") {
    const percent = coPreloadTotal > 0 ? Math.round((coPreloadCurrent / coPreloadTotal) * 100) : 0;
    return (
      <div className="flex flex-col items-center justify-center py-32 max-w-md mx-auto space-y-8 animate-in fade-in duration-500">
        <Card className="border-none shadow-2xl bg-white/80 backdrop-blur-md p-8 rounded-3xl text-center space-y-6 w-full border border-gray-100">
          <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto text-3xl shadow-inner relative">
            <Headphones className="w-10 h-10 text-indigo-600 animate-pulse" />
            <div className="absolute inset-0 rounded-full border-4 border-indigo-600/30 border-t-indigo-600 animate-spin" />
          </div>
          <CardHeader className="p-0">
            <CardTitle className="text-2xl font-black text-gray-900 tracking-tight">Pré-carregando mídias...</CardTitle>
            <CardDescription className="text-sm font-semibold text-gray-500 mt-2">
              Garantindo que todos os áudios e imagens estejam prontos para o seu teste de compreensão oral.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-0">
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs font-bold text-gray-500">
                <span>Progresso</span>
                <span>{coPreloadCurrent} de {coPreloadTotal} ({percent}%)</span>
              </div>
              <Progress value={percent} className="h-2.5 bg-gray-100 rounded-full" />
            </div>
            {questions[coPreloadCurrent - 1] && (
              <p className="text-xs font-medium text-indigo-600 bg-indigo-50/50 py-2 px-4 rounded-xl inline-block max-w-full truncate">
                {questions[coPreloadCurrent - 1]?.title || `Questão ${coPreloadCurrent}`}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (activeType === "listening" && coPreloadStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center py-32 max-w-md mx-auto space-y-8 animate-in fade-in duration-500">
        <Card className="border-none shadow-2xl bg-white/80 backdrop-blur-md p-8 rounded-3xl text-center space-y-6 w-full border border-red-100">
          <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto text-3xl shadow-inner">
            <AlertTriangle className="w-10 h-10 text-red-600 animate-bounce" />
          </div>
          <CardHeader className="p-0">
            <CardTitle className="text-2xl font-black text-red-600 tracking-tight">Erro ao carregar mídias</CardTitle>
            <CardDescription className="text-sm font-semibold text-gray-500 mt-2">
              Não foi possível iniciar a prova de compreensão oral pois algumas mídias obrigatórias estão quebradas ou ausentes.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="bg-red-50/50 border border-red-100 rounded-2xl p-4 text-left max-h-60 overflow-y-auto text-xs text-red-700 font-mono space-y-2 whitespace-pre-wrap">
              {coPreloadErrorMessage}
            </div>
          </CardContent>
          <CardFooter className="p-0 flex gap-3">
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="flex-1 rounded-xl">
              Voltar ao Dashboard
            </Button>
            <Button onClick={() => runCoPreloading(questions)} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Tentar Novamente
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-48 space-y-4 animate-in fade-in duration-500">
        <AlertTriangle className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold text-gray-900">Nenhuma questão disponível para esta seção</h2>
        <p className="text-sm text-gray-500">Não há questões cadastradas para esta seção no momento.</p>
        <Button onClick={() => navigate("/dashboard")} variant="outline" className="mt-4">
          Voltar ao Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-in fade-in duration-500 pb-12">
      {/* Top Banner Status */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-md bg-primary/10 text-primary">
            {mode === "full_exam" ? `SIMULADO COMPLETO - ${getCompetencyLabel(fullExamStage).toUpperCase()}` : getCompetencyLabel(activeType).toUpperCase()}
          </span>
          <span className="text-sm font-semibold text-gray-500">
            {getDisplaySection(currentQuestion)}
          </span>
        </div>
        
        {/* Progress bar in-between */}
        <div className="flex-1 max-w-xs w-full">
          <div className="flex justify-between text-[11px] font-semibold text-gray-400 mb-1">
            <span>Progresso</span>
            <span>
              {isWritingCompetency(activeType) ? `Tarefa ${currentQuestionIdx + 1} de ${questions.length}: ${getDisplaySection(currentQuestion)}` : `Questão ${currentQuestionIdx + 1} de ${questions.length}`}
            </span>
          </div>
          <Progress value={progressPercent} className="h-1.5" />
        </div>

        {/* Timer */}
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-xl font-bold font-mono text-sm">
          <Clock className="w-4 h-4 text-red-500" />
          <span>{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Main Panel layout */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Question Panel */}
        <div className="md:col-span-2 space-y-6">
          <Card className="border-none shadow-sm bg-white overflow-hidden">
            <CardHeader className="bg-gray-50/50 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle className="text-base font-bold text-gray-900">{t("testRunner.question", "Questão")} {currentQuestionIdx + 1}</CardTitle>
                  {currentQuestion?.title && (
                    <h4 className="text-sm font-semibold text-gray-700 mt-1.5 bg-gray-100/50 px-2.5 py-1 rounded-md w-fit border border-gray-200/40">
                      {currentQuestion.title}
                    </h4>
                  )}
                </div>
                {activeType !== "writing" && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleToggleReview}
                    className={`rounded-lg h-8 text-xs shrink-0 ${reviewMarked[currentQuestionIdx] ? "text-amber-500 hover:text-amber-600 bg-amber-50" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Bookmark className="w-4 h-4 mr-1.5" />
                    {t("testRunner.reviewLater", "Revisar depois")}
                  </Button>
                )}
              </div>
              {/* Question Instruction / Command */}
              {(() => {
                const questionInstruction = currentQuestion ? (
                  currentQuestion.instruction || ""
                ) : "";
                
                if (!questionInstruction) return null;
                
                if (activeType === "writing") {
                  return (
                    <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100/50 text-sm text-gray-700 font-medium leading-relaxed whitespace-pre-wrap mt-3 animate-in fade-in duration-300">
                      {questionInstruction}
                    </div>
                  );
                }
                
                return (
                  <p className="text-xs text-gray-500 mt-2 italic leading-relaxed">
                    {questionInstruction}
                  </p>
                );
              })()}
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Listening simulated Player */}
              {activeType === "listening" && currentQuestion?.hasAudio && (() => {
                const audioUrl = currentQuestion?.audioUrl;
                const qKey = currentQuestion.id || String(currentQuestion.importOrder || currentQuestion.metadata?.importOrder || currentQuestionIdx);
                const audioSrc = preloadedAudios[qKey] || resolveAudioSrc(currentQuestion, resolvedMediaUrls);
                if (isBareMediaName(audioUrl) || isBareMediaName(audioSrc)) {
                  return (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700 font-semibold flex items-center gap-2 animate-in fade-in duration-300">
                      <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                      {t("testRunner.audioNotFound", "Áudio não encontrado para esta questão.")}
                    </div>
                  );
                }
                return (
                  <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-xl flex flex-col gap-3 animate-in fade-in duration-300">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white cursor-pointer relative">
                        <Headphones className="w-5 h-5" />
                        {audioLoading && (
                          <div className="absolute inset-0 rounded-full bg-blue-600/80 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-blue-800 flex items-center gap-2">
                          {t("testRunner.examAudio", "Áudio do Exame")}
                          {audioLoading && (
                            <span className="text-[10px] text-blue-600 font-normal animate-pulse flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> {t("testRunner.loadingAudio", "Carregando áudio...")}
                            </span>
                          )}
                          {audioReady && !audioLoading && (
                            <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100 animate-in fade-in duration-300">
                              <CheckCircle2 className="w-3 h-3 animate-pulse" /> {t("common.ready", "Pronto")}
                            </span>
                          )}
                          {audioError && (
                            <span className="text-[10px] text-rose-600 font-medium flex items-center gap-0.5 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100 animate-in fade-in duration-300">
                              <AlertTriangle className="w-3 h-3 animate-bounce" /> {audioError}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-gray-500 mt-0.5 block">{t("testRunner.examAudioDesc", "Ouça a gravação de áudio e responda à questão.")}</span>
                      </div>
                    </div>
                    {(() => {
                      if (audioSrc) {
                        return (
                          <audio 
                            key={`audio-${currentQuestion.id}-${audioSrc}`}
                            controls 
                            src={audioSrc} 
                            className="w-full h-10 mt-1 rounded-lg bg-white" 
                            onLoadStart={() => {
                              audioLoadStartTimeRef.current = performance.now();
                              setAudioLoading(true);
                              setAudioReady(false);
                              setAudioError(null);
                            }}
                            onCanPlay={() => {
                              setAudioLoading(false);
                              setAudioReady(true);
                              console.log("[CO_AUDIO_LOAD_PERFORMANCE]", { 
                                loadTimeMs: performance.now() - audioLoadStartTimeRef.current,
                                title: currentQuestion.title || currentQuestion.importOrder
                              });
                            }}
                            onError={() => {
                              setAudioLoading(false);
                              setAudioError(t("testRunner.audioLoadError", "Erro ao carregar o áudio."));
                            }}
                          />
                        );
                      }
                      console.warn("[CO_AUDIO_MISSING_AT_RENDER]", {
                        id: currentQuestion.id,
                        title: currentQuestion.title,
                        resolvedAudio: audioUrl,
                        rawQuestion: currentQuestion,
                      });
                      return <div className="text-[10px] text-red-500 font-medium italic text-center py-1">{t("testRunner.audioNotFound", "Áudio não encontrado para esta questão.")}</div>;
                    })()}
                  </div>
                );
              })()}

              {/* Supportive Illustrative Image */}
              {(() => {
                const hasValidUrl = (currentQuestion?.imageMediaId && resolvedMediaUrls[currentQuestion.imageMediaId]) ||
                  currentQuestion?.imageUrl || currentQuestion?.supportImage || currentQuestion?.support_image || currentQuestion?.metadata?.supportImage || currentQuestion?.metadata?.imageUrl;
                if (!currentQuestion?.hasImage || !hasValidUrl) return null;
                return (
                  <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white p-2 min-h-[200px] flex items-center justify-center">
                    {currentQuestion.imageMediaId && resolvedMediaUrls[currentQuestion.imageMediaId] ? (
                      <img src={resolvedMediaUrls[currentQuestion.imageMediaId]} alt={currentQuestion.title} className="max-w-full h-auto max-h-[70vh] object-contain animate-in fade-in duration-300" />
                    ) : (
                      <img src={currentQuestion.imageUrl || currentQuestion.supportImage || currentQuestion.support_image || currentQuestion.metadata?.supportImage || currentQuestion.metadata?.imageUrl} alt={currentQuestion.title} className="max-w-full h-auto max-h-[70vh] object-contain animate-in fade-in duration-300" />
                    )}
                  </div>
                );
              })()}

              {/* Visible Context (Analysis/Context part of the question) */}
              {(() => {
                const context = getVisibleContext(currentQuestion);
                if (!context) return null;
                return (
                  <div className="bg-blue-50/30 p-5 rounded-2xl border border-blue-100/50 text-sm leading-relaxed text-gray-800 italic animate-in fade-in duration-300">
                    <span className="font-bold block mb-1 uppercase text-[10px] tracking-wider text-blue-600">Contexte / Analyse :</span>
                    {context}
                  </div>
                );
              })()}

              {/* Specific Question Prompt */}
              {(() => {
                const prompt = getQuestionPrompt(currentQuestion);
                if (!prompt) {
                  if (activeType !== "writing" && isAdmin) {
                    return <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-100 italic">Pergunta específica não informada.</div>;
                  }
                  return null;
                }
                return (
                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5 text-indigo-950 shadow-sm animate-in slide-in-from-top-2 duration-300">
                    <h3 className="text-base font-black leading-tight">{prompt}</h3>
                  </div>
                );
              })()}

              {/* Text Card content (Source text/Body) */}
              {(() => {
                const { body: displayText } = resolveDisplayedQuestion(currentQuestion);
                if (!displayText || isExplanationText(displayText)) return null;

                return (
                  <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100 text-sm font-serif text-gray-800">
                    <JustifiedQuestionText text={displayText} />
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Answer Area */}
          {activeType === "writing" ? (
            <Card className="border-none shadow-sm bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-bold">Área de Produção Textual</CardTitle>
                <CardDescription>Escreva diretamente na caixa de texto abaixo em francês.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Writing section info panel */}
                {(() => {
                  const sectionKey = currentQuestion?.sectionKey || normalizeWritingSection(currentQuestion?.section || "");
                  const config = getWritingConfigByKey(sectionKey);
                  if (!config) return null;
                  const wordCount = writingText.trim().split(/\s+/).filter(Boolean).length;
                  const belowMinimum = wordCount < config.minimumWords;
                  return (
                    <div className={`rounded-xl border p-3.5 flex flex-wrap gap-4 text-xs ${belowMinimum ? "bg-amber-50/60 border-amber-100" : "bg-emerald-50/40 border-emerald-100"}`}>
                      <div className="flex items-center gap-1.5">
                        <PenLine className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="font-bold text-gray-700">{config.taskLabel}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`font-semibold ${belowMinimum ? "text-amber-700" : "text-gray-500"}`}>
                          Mínimo: {config.minimumWords} palavras
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Sugerido: {config.suggestedTimeMinutes} min</span>
                      </div>
                    </div>
                  );
                })()}

                <Textarea
                  ref={textareaRef}
                  placeholder="Écrivez votre texte ici..."
                  className="min-h-[300px] font-mono leading-relaxed bg-gray-50 border-none resize-none focus-visible:ring-transparent text-sm p-4"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  value={writingText}
                  onChange={(e) => handleWritingTextChange(e.target.value)}
                />
                
                {/* Writing metrics/actions bar */}
                {(() => {
                  const sectionKey = currentQuestion?.sectionKey || normalizeWritingSection(currentQuestion?.section || "");
                  const config = getWritingConfigByKey(sectionKey);
                  const wordCount = writingText.trim().split(/\s+/).filter(Boolean).length;
                  const belowMin = config && wordCount < config.minimumWords;
                  return (
                    <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="font-bold text-gray-700">Palavras:</span>
                        <span className={`px-2 py-0.5 rounded-md font-mono font-bold ${belowMin ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                          {wordCount} {config ? `/ ${config.minimumWords} mín.` : "palavras"}
                        </span>
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        className="rounded-lg text-xs bg-white border-gray-200"
                        onClick={handleSaveSelectedText}
                      >
                        Salvar Palavra/Frase
                      </Button>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

          ) : (
            <Card className="border-none shadow-sm bg-white">
              <CardHeader>
                <CardTitle className="text-sm font-bold">Selecione a Alternativa</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {shouldRenderImageOptions(currentQuestion) ? (
                  // Rendering image-based alternatives for Listening Section A
                  <div key={currentQuestion.id} className="grid grid-cols-2 gap-4">
                    {(currentQuestion.options || []).map((opt: any) => {
                      const isSelected = selectedAnswers[currentQuestionIdx] === opt.label;
                      const optionLabel = opt.label || opt.id || opt.value;
                      const resolvedUrl = resolveOptionImageSrc(currentQuestion, opt, { ...resolvedMediaUrls, ...coPreloadedUrls });
                      const optionImage =
                        resolvedUrl ||
                        (opt.image && coPreloadedUrls[opt.image]) ||
                        (opt.imageUrl && coPreloadedUrls[opt.imageUrl]) ||
                        opt.image ||
                        opt.imageUrl ||
                        getOptionImageUrl(currentQuestion, optionLabel);

                      const isBare = isBareMediaName(optionImage) || isBareMediaName(opt.image) || isBareMediaName(opt.imageUrl);

                      if (!optionImage) {
                        console.warn("[CO_IMAGE_MISSING_AT_RENDER]", {
                          id: currentQuestion.id,
                          title: currentQuestion.title,
                          optionLabel,
                          resolvedImage: optionImage,
                          rawOption: opt,
                          rawQuestion: currentQuestion,
                        });
                      }

                      return (
                        <button
                          key={`${currentQuestion.id}-${opt.label}`}
                          onClick={() => handleSelectAnswer(opt.label)}
                          className={`relative overflow-hidden rounded-xl border-2 p-1 transition-all duration-200 aspect-[4/3] flex items-center justify-center group ${
                            isSelected
                              ? "border-primary ring-2 ring-primary/20 bg-primary/5 shadow-md"
                              : "border-gray-100 hover:border-primary/50 hover:bg-gray-50/50"
                          }`}
                        >
                          {isBare || !optionImage ? (
                            <div className="text-gray-400 text-xs font-medium">Sem imagem</div>
                          ) : (
                            <img src={optionImage} alt={`Alternativa ${opt.label}`} className="w-full h-full object-cover rounded-lg" />
                          )}
                          <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shadow-sm transition-all ${
                            isSelected ? "bg-primary text-white" : "bg-white/80 backdrop-blur border border-gray-200 text-gray-600 group-hover:bg-primary group-hover:text-white group-hover:border-primary"
                          }`}>
                            {opt.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Rendering textual alternatives (For CE and all other CO sections)
                  <div key={currentQuestion.id} className="space-y-3">
                    {(() => {
                      const textAlternatives = getQuestionTextAlternatives(currentQuestion);
                      return Object.entries(textAlternatives).map(([key, value]) => {
                        const isSelected = selectedAnswers[currentQuestionIdx] === key;
                        return (
                          <button
                            key={`${currentQuestion.id}-${key}`}
                            onClick={() => handleSelectAnswer(key)}
                            className={`w-full flex items-center gap-4 p-4 text-left rounded-xl border transition-all duration-200 font-medium ${
                              isSelected 
                                ? "border-primary bg-primary/5 text-primary shadow-sm" 
                                : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 text-gray-700"
                            }`}
                          >
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                              isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
                            }`}>
                              {key}
                            </span>
                            <span className="text-sm">{value}</span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar overview navigations */}
        <div className="space-y-6">
          {activeType !== "writing" ? (
            <Card className="border-none shadow-sm bg-white">
              <CardHeader>
                <CardTitle className="text-sm font-bold">{t("testRunner.testMap", "Mapa da Prova")}</CardTitle>
                <CardDescription>{t("testRunner.completionSummary", "Resumo de preenchimento")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {questions.map((_, idx) => {
                    const isCurrent = currentQuestionIdx === idx;
                    const isAnswered = activeType === "writing" ? writingText.trim().length > 0 : selectedAnswers[idx] !== undefined;
                    const isMarked = reviewMarked[idx];

                    return (
                      <button
                        key={idx}
                        onClick={() => setCurrentQuestionIdx(idx)}
                        className={`h-10 rounded-xl flex items-center justify-center font-bold text-xs transition-all ${
                          isCurrent ? "bg-primary text-white shadow-md ring-2 ring-primary/20" :
                          isMarked ? "bg-amber-100 text-amber-700 hover:bg-amber-200" :
                          isAnswered ? "bg-green-100 text-green-700 hover:bg-green-200" :
                          "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <div className="w-3 h-3 bg-primary rounded" />
                    <span>{t("testRunner.currentQuestion", "Questão Atual")}</span>
                  </div>
                  {activeType !== "writing" && (
                    <>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500">
                        <div className="w-3 h-3 bg-green-100 rounded" />
                        <span>{t("testRunner.answered", "Respondido")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500">
                        <div className="w-3 h-3 bg-amber-100 rounded" />
                        <span>{t("testRunner.markedForReview", "Marcado para Revisar")}</span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-none shadow-sm bg-white">
              <CardHeader>
                <CardTitle className="text-sm font-bold">Expression Écrite</CardTitle>
                <CardDescription>
                  {questions.length === 1 
                    ? `Section ${questions[0].section?.includes("A") ? "A" : "B"} individual`
                    : "Teste completo EE"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-gray-600">
                  {questions.length === 1 
                    ? "1 tarefa de escrita"
                    : "Section A → Section B"}
                </p>
                {questions.length > 1 && (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className={`p-2 rounded-lg text-xs font-medium ${currentQuestionIdx === 0 ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                      Etapa 1: Section A
                    </div>
                    <div className={`p-2 rounded-lg text-xs font-medium ${currentQuestionIdx === 1 ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                      Etapa 2: Section B
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Footer Button panels */}
          <div className="space-y-3">
            {activeType === "writing" ? (
              <div className="flex gap-2">
                {questions.length > 1 && currentQuestionIdx === 0 && (
                  <Button 
                    onClick={handleNext} 
                    className="flex-1 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  >
                    Ir para Section B
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                {questions.length > 1 && currentQuestionIdx === 1 && (
                  <Button 
                    onClick={() => setCurrentQuestionIdx(0)} 
                    className="flex-1 rounded-xl bg-white border-purple-600 text-purple-600 hover:bg-purple-50 font-bold border-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar para Section A
                  </Button>
                )}
                {questions.length === 1 && (
                  <div className="flex-1 h-10" />
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handlePrev} 
                  disabled={currentQuestionIdx === 0}
                  className="flex-1 rounded-xl bg-white border-gray-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("testRunner.previous", "Anterior")}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleNext} 
                  disabled={currentQuestionIdx === questions.length - 1}
                  className="flex-1 rounded-xl bg-white border-gray-200"
                >
                  {t("testRunner.next", "Próxima")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
            
            <Button 
              onClick={handleFinishButtonClick}
              disabled={isSavingAndCorrecting || (activeType === "writing" && questions.length > 1 && currentQuestionIdx === 0)}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-md py-6 flex items-center justify-center gap-2"
            >
              {isSavingAndCorrecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t("testRunner.finishing", "Finalizando...")}</span>
                </>
              ) : activeType === "writing" ? (
                questions.length === 1 
                  ? t("testRunner.finishSection", "Finalizar Section {{section}}", { section: questions[0].section?.includes("A") ? "A" : "B" })
                  : currentQuestionIdx === 1 
                    ? t("testRunner.finishEETest", "Finalizar teste EE") 
                    : t("testRunner.finishFillSectionB", "Finalizar (Preencha a Section B também)")
              ) : mode === "full_exam" ? (
                fullExamStage === "writing" ? t("testRunner.finishFullSimulated", "Finalizar simulado") : t("testRunner.finishAndProceed", "Finalizar e prosseguir")
              ) : (
                t("testRunner.finishTest", "Finalizar teste")
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Modal B — Unanswered Questions */}
      {showUnansweredModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center">
                <CheckSquare className="w-8 h-8 text-amber-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-gray-900">Existem questões sem resposta</h2>
                <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
                  Você deixou <span className="font-semibold text-amber-600">{unansweredCount} questão(ões)</span> sem responder.
                  Deseja finalizar mesmo assim?
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={() => setShowUnansweredModal(false)}
                className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl py-6 font-semibold shadow-md"
              >
                Voltar ao teste
              </Button>
              <Button
                onClick={() => {
                  setShowUnansweredModal(false);
                  handleFinish();
                }}
                variant="outline"
                className="flex-1 border-gray-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl py-6 font-semibold"
              >
                Finalizar mesmo assim
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal C — Minimum word count warning */}
      {showWordWarningModal && wordWarningData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-gray-900">Produção abaixo do mínimo</h2>
                <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
                  Esta tarefa tem mínimo sugerido de{" "}
                  <span className="font-semibold text-amber-600">{wordWarningData.minimum} palavras</span>.
                  Sua produção tem{" "}
                  <span className="font-semibold text-gray-800">{wordWarningData.current} palavras</span>.
                  Deseja continuar mesmo assim?
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={() => setShowWordWarningModal(false)}
                className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl py-6 font-semibold shadow-md"
              >
                Voltar para escrever
              </Button>
              <Button
                onClick={wordWarningData.onContinue}
                variant="outline"
                className="flex-1 border-gray-200 text-amber-600 hover:bg-amber-50 hover:text-amber-700 rounded-xl py-6 font-semibold"
              >
                Continuar mesmo assim
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal D — Finish Confirmation */}
      {showConfirmFinishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 space-y-6 animate-in zoom-in-95 duration-200">
            {isSavingAndCorrecting ? (
              <div className="flex flex-col items-center text-center space-y-6 py-6">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
                <div className="space-y-2">
                  <h3 className="text-lg font-black text-gray-900">
                    Finalizando teste...
                  </h3>
                  <p className="text-xs text-gray-500 max-w-xs mx-auto leading-relaxed">
                    Salvando seus resultados e montando seu painel de simulado.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-blue-600 animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-bold text-gray-900">Finalizar teste?</h2>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
                      Você deseja finalizar o teste e enviar suas respostas agora?
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    onClick={() => setShowConfirmFinishModal(false)}
                    variant="outline"
                    className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl py-6 font-semibold"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => executeFinishTest(false)}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl py-6 font-semibold shadow-md"
                  >
                    Finalizar teste
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
